import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import { extractYouTubeId, extractVimeoId, detectVideoType } from '../../utils/videoPortfolio';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import Messaging, { countUnreadConversations } from '../../components/Messaging';
import SupportInbox from '../../components/SupportInbox';
import ResourceFinder from '../../components/ResourceFinder';
import LicensingCerts from '../../components/LicensingCerts';
import CareerPayGuide from '../../components/CareerPayGuide';
import TradeHub from '../../components/TradeHub';
import NavDropdown from '../../components/NavDropdown';
import { startConversation } from '../../utils/startConversation';
import NotificationBell from '../../components/NotificationBell';
import { sendNotificationEmail, notifyUnionStaff } from '../../utils/sendNotificationEmail';
import DeleteAccountModal from '../../components/DeleteAccountModal';
import HireCelebrationModal from '../../components/HireCelebrationModal';
import ImageCropModal from '../../components/ImageCropModal';
import { renderJobDescription } from '../../utils/renderDescription';
import { milesBetweenZips } from '../../utils/distance';
import LegalLinksRow from '../../components/LegalLinksRow';
import { joinParts } from '../../utils/joinParts';
import { getInterviewCountdown, URGENCY_COLORS } from '../../utils/interviewCountdown';
import { parseExamDate } from '../../utils/examDates';
import ExploreOtherOptions from '../../components/ExploreOtherOptions';
import CheckInPanel from '../../components/CheckInPanel';
import ZoomableImg from '../../components/ZoomableImg';
import ApprenticeshipPrerequisites from '../../components/ApprenticeshipPrerequisites';
import ApprenticeshipResourceLink from '../../components/ApprenticeshipResourceLink';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import { WorkerJobCompare, WorkerOfferCompare, WorkerApprenticeshipCompare } from '../../components/compare/WorkerCompare';
import { CompareToggle, CompareEntryButton } from '../../components/compare/CompareOverlay';
import { useCompareSelection } from '../../utils/useCompareSelection';
import { usePressFeedback } from '../../utils/usePressFeedback';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const NAV_H     = 56;
const SIDEBAR_W = 190;

// `labelKey` resolved via t() at render time -- object keys (pending/reviewing/etc.)
// stay stable internal status values, matched against apprenticeshipStatusMap.
const APPR_STATUS_BADGE = {
  pending:   { labelKey: 'worker.dashboard.apprenticeships.statusPendingReview', bg: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.75)' },
  reviewing: { labelKey: 'worker.dashboard.apprenticeships.statusReviewing',     bg: AMBER,    color: NAVY },
  accepted:  { labelKey: 'worker.dashboard.apprenticeships.statusAccepted',      bg: GREEN,    color: WHITE },
  rejected:  { labelKey: 'worker.dashboard.applications.statusNotSelected',      bg: '#5C4040', color: '#E0B3B3' },
};

// `value` is stored verbatim in worker_trades.trade and matched (case-insensitive)
// against apprenticeship/job `.trade` fields (see the Apprenticeships and Jobs tabs
// below) -- must stay stable English regardless of UI language. Only the rendered
// label is translated, via the shared worker.tradeNames.* keys also used by the
// signup flow's own (slightly larger) trade list in WorkerStep3.jsx.
const TRADE_OPTIONS = [
  { value: 'Electrician',            key: 'electrician' },
  { value: 'Plumber',                key: 'plumber' },
  { value: 'Welder and Fabricator',  key: 'welderFabricator' },
  { value: 'HVAC Technician',        key: 'hvacTechnician' },
  { value: 'Carpenter',              key: 'carpenter' },
  { value: 'CNC Operator',           key: 'cncOperator' },
  { value: 'Diesel Mechanic',        key: 'dieselMechanic' },
  { value: 'Industrial Maintenance', key: 'industrialMaintenance' },
  { value: 'Pipefitter',             key: 'pipefitter' },
  { value: 'Sheet Metal Worker',     key: 'sheetMetalWorker' },
  { value: 'CDL/Truck Driver',       key: 'cdlTruckDriver' },
];

// Applications badge: an application counts as seen only at the status it had when the worker
// last opened the Applications tab, so a later change (reviewing -> offer_sent) re-triggers
// the badge. Keyed `${id}:${status}`; the old 'seenAppsIds' held bare ids, which are carried
// over once at each app's current status so existing users don't get a spurious badge.
const APPS_BADGE_STATUSES = ['offer_sent', 'reviewing', 'rejected'];
const SEEN_APPS_KEY = 'seenAppsStatusKeys';
const appSeenKey = a => `${a.id}:${a.status}`;
function hasUnseenApps(apps) {
  // Nothing actionable (or the query failed): nothing to badge, and no legacy migration off an empty list.
  if (apps.length === 0) return false;
  let seen;
  try {
    const stored = localStorage.getItem(SEEN_APPS_KEY);
    if (stored !== null) {
      seen = JSON.parse(stored);
    } else {
      const legacyIds = JSON.parse(localStorage.getItem('seenAppsIds') || '[]');
      seen = apps.filter(a => legacyIds.includes(a.id)).map(appSeenKey);
      localStorage.setItem(SEEN_APPS_KEY, JSON.stringify(seen));
      localStorage.removeItem('seenAppsIds');
    }
  } catch (_) {
    seen = [];
  }
  return apps.some(a => !seen.includes(appSeenKey(a)));
}

const DISMISS_REASONS = [
  'Pay is too low for the role',
  'Shift/schedule doesn’t work for me',
  'Location is too far',
  'Benefits don’t meet my needs',
  'Travel requirements don’t work for me',
  'Other',
];

// `value` is stored verbatim as dismissReason (persisted to the job_dismissals `reason`
// column) and also compared against 'Other' to show/hide the free-text detail field --
// it must stay stable English regardless of UI language, same pattern as WorkerStep3's
// TRADES. Only the rendered button label is translated.
const DISMISS_REASON_OPTIONS = DISMISS_REASONS.map((value, i) => ({
  value,
  key: ['reasonPayTooLow', 'reasonScheduleDoesntWork', 'reasonLocationTooFar', 'reasonBenefitsDontMeetNeeds', 'reasonTravelDoesntWork', 'reasonOther'][i],
}));

function IconGrid({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function IconSearch({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconMsg({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconUser({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconResources({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
function IconDollar({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function Avatar({ initials, size = 36, photoUrl }) {
  if (photoUrl) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: AMBER }}>
        <ZoomableImg src={photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: AMBER, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontWeight: 700, fontSize: size * 0.34, color: NAVY, fontFamily: FONT_BODY }}>
        {initials}
      </span>
    </div>
  );
}


// tab identifiers (e.g. 'Jobs', 'Saved Jobs') double as both the internal activeTab state
// value AND, in a few places below, the literal rendered button text -- translating display
// text without breaking those equality checks means keeping the identifiers as-is and only
// mapping through this lookup wherever the identifier is actually shown to the user.
const TAB_LABEL_KEYS = {
  'Dashboard': 'nav.worker.dashboard',
  'Resources': 'nav.worker.resources',
  'Jobs': 'nav.worker.jobs',
  'Apprenticeships': 'nav.worker.apprenticeships',
  'Applications': 'nav.worker.applications',
  'Saved Jobs': 'nav.worker.savedJobs',
  'School Finder': 'nav.worker.schoolFinder',
  'Union Finder': 'nav.worker.unionFinder',
  'Licensing & Certs': 'nav.worker.licensingCerts',
  'Career Pay Guide': 'nav.worker.careerPayGuide',
  'Union Wages': 'resources.tabs.unionWages',
  'Messages': 'nav.worker.messages',
  'My Profile': 'nav.worker.myProfile',
  'Experience': 'nav.worker.experience',
  'Check-ins': 'nav.worker.checkins',
  'Settings': 'nav.worker.settings',
};

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };

// Desktop top-nav structure: flat entries render as a single tab button, entries
// with `tabs` render as a NavDropdown grouping those child tabs. `activeTab`
// state still holds the exact same leaf tab names as before (e.g. 'Jobs',
// 'Applications') -- this is purely a nav-layout regrouping, not a routing change.
const RESOURCE_TABS = ['School Finder', 'Union Finder', 'Licensing & Certs', 'Career Pay Guide', 'Union Wages'];
const NAV_GROUPS = [
  { label: 'Dashboard', tab: 'Dashboard' },
  { label: 'Jobs', tabs: ['Jobs', 'Apprenticeships', 'Applications', 'Saved Jobs'] },
  // 'Union Wages' isn't a dashboard tab: it opens the public /union-wages page (see openTab).
  { label: 'Resources', tabs: RESOURCE_TABS },
  { label: 'Messages', tab: 'Messages' },
  { label: 'My Profile', tab: 'My Profile' },
  { label: 'Experience', tab: 'Experience' },
  { label: 'Check-ins', tab: 'Check-ins' },
  { label: 'Settings', tab: 'Settings' },
];

function parseCertsRequired(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; }
  catch { return typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : []; }
}

// Ordinal rank across the 5 canonical experience buckets shared by worker_trades.years_experience
// and jobs.min_years_experience. Values outside this vocabulary (e.g. legacy free-text data)
// return null -- treated as "unknown" by computeMatchTier, never as an auto-fail.
const EXPERIENCE_RANK = {
  'less than 1 year': 0,
  '1 to 3 years':     1,
  '4 to 6 years':     2,
  '7 to 10 years':    3,
  '10+ years':        4,
};
function experienceRank(val) {
  if (!val || typeof val !== 'string') return null;
  const rank = EXPERIENCE_RANK[val.toLowerCase().trim()];
  return rank === undefined ? null : rank;
}

// Match Quality tiers ('great' | 'good' | 'okay' | null) for a worker/job pair.
// null means the job doesn't qualify as a match at all (trade, required-cert, or
// required-license gate failed) -- callers should render no badge in that case, not an "okay" badge.
function computeMatchTier(job, workerTrade, workerYearsExperience, workerCertNames, workerLicenseNames) {
  if (!workerTrade || !job.trade_category || job.trade_category.toLowerCase().trim() !== workerTrade.toLowerCase().trim()) {
    return null;
  }
  const certSet = new Set((workerCertNames || []).map(n => n.toLowerCase().trim()));
  const required = parseCertsRequired(job.certifications_required);
  if (!required.every(c => certSet.has(c.toLowerCase().trim()))) return null;

  const licenseSet = new Set((workerLicenseNames || []).map(n => n.toLowerCase().trim()));
  const requiredLicenses = parseCertsRequired(job.licenses_required);
  if (!requiredLicenses.every(l => licenseSet.has(l.toLowerCase().trim()))) return null;

  const jobRank = experienceRank(job.min_years_experience);
  const workerRank = experienceRank(workerYearsExperience);
  const experienceSatisfied = job.willing_to_train === true || jobRank === null || workerRank === null
    ? true
    : workerRank >= jobRank;

  if (!experienceSatisfied) return 'okay';
  const preferred = parseCertsRequired(job.certifications_preferred);
  const hasAllPreferred = preferred.every(c => certSet.has(c.toLowerCase().trim()));
  return hasAllPreferred ? 'great' : 'good';
}

// Intensity ramps from solid amber (Great) down to the same neutral tint used by the
// per-diem/travel/benefits info pills (Okay), so the three tiers read as a scale at a
// glance and stay visually distinct from Urgently Hiring (solid red) and Will Train
// (navy fill + amber outline).
const MATCH_TIER_STYLE = {
  great: { background: AMBER, color: NAVY, border: 'none' },
  good:  { background: 'rgba(245,166,35,0.15)', color: AMBER, border: `1px solid ${AMBER}` },
  okay:  { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)', border: 'none' },
};
const MATCH_TIER_LABEL_KEY = {
  great: 'worker.dashboard.jobs.matchGreat',
  good:  'worker.dashboard.jobs.matchGood',
  okay:  'worker.dashboard.jobs.matchOkay',
};
function MatchTierBadge({ tier, t }) {
  if (!tier) return null;
  return (
    <span style={{ fontSize: '10px', fontWeight: 700, borderRadius: '999px', padding: '2px 8px', ...MATCH_TIER_STYLE[tier] }}>
      {t(MATCH_TIER_LABEL_KEY[tier])}
    </span>
  );
}

// worker_trades.availability doubles as the worker's schedule preference -- it already
// shares almost the exact vocabulary as jobs.job_type. No preference set, or "Open to
// all", never filters anything out.
function matchesScheduleFilter(job, workerAvailability) {
  if (!workerAvailability || workerAvailability === 'Open to all') return true;
  return (job.job_type || '').toLowerCase().trim() === workerAvailability.toLowerCase().trim();
}

// A job with no ZIP, or a worker with no ZIP, is "unknown distance" -- never auto-excluded,
// same "unknown never fails" principle used for experience/cert gates elsewhere in this file.
function matchesRadiusFilter(job, workerZip, radiusMiles) {
  if (!radiusMiles) return true;
  const d = milesBetweenZips(workerZip, job.zip);
  return d === null || d <= radiusMiles;
}

export default function WorkerDashboard() {
  usePressFeedback();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tabLabel = (tab) => TAB_LABEL_KEYS[tab] ? t(TAB_LABEL_KEYS[tab]) : tab;

  // Mobile bottom nav: 5 top-level icons mirroring the desktop's grouped structure.
  // 'jobs' and 'resources' expand the same sub-tab-bar mechanism 'profile' already
  // used (see mobileJobsOpen / mobileResourcesOpen), rather than a flat per-tab icon
  // each -- dropdowns don't translate to a bottom nav, so grouping expands instead.
  const NAV_ITEMS = [
    { key: 'dashboard',  label: t('nav.worker.dashboard'), Icon: IconGrid },
    { key: 'jobs',       label: t('nav.worker.jobs'),      Icon: IconSearch },
    { key: 'resources',  label: t('nav.worker.resources'), Icon: IconResources },
    { key: 'messages',   label: t('nav.worker.messages'),  Icon: IconMsg },
    { key: 'profile',    label: t('nav.worker.profile'),   Icon: IconUser },
  ];

  // Existing state
  const [refreshKey, setRefreshKey]     = useState(0);
  const [worker, setWorker]             = useState(null);
  useLanguagePreference(worker?.id);
  const [profile, setProfile]           = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [trades, setTrades]             = useState(null);
  const [jobs, setJobs]                 = useState([]);
  const [matchedJobStatusByJobId, setMatchedJobStatusByJobId] = useState({});
  const [expandedJob, setExpandedJob]   = useState(null);
  const [employerDetailsCache, setEmployerDetailsCache] = useState({});
  const [appliedJobs, setAppliedJobs]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    return requestedTab && TAB_LABEL_KEYS[requestedTab] ? requestedTab : 'Dashboard';
  });
  // Deep-link seed for Career Pay Guide, set when a School/Union Finder pay snippet is
  // clicked -- CareerPayGuide clears this itself once applied (see onInitialTradeApplied)
  // so a later direct visit to the tab doesn't "stick" on a stale trade.
  const [payGuideInitialTrade, setPayGuideInitialTrade] = useState(null);
  const goToTradePay = (tradeName) => { setActiveTab('Career Pay Guide'); setPayGuideInitialTrade(tradeName); };
  // Trade Hub overlay -- independent of activeTab, opened from a Licensing & Certs card or a
  // Career Pay Guide detail view. Closing it just clears this state and returns to whatever
  // tab was open underneath.
  const [activeTradeHub, setActiveTradeHub] = useState(null);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [now, setNow]                               = useState(() => new Date());
  const [openConversationId, setOpenConversationId] = useState(null);
  const [unreadCount, setUnreadCount]       = useState(0);
  const [mobileMessageBadge, setMobileMessageBadge] = useState(0);
  // Phone-width chat thread open: the Messages tab drops the support row, setup banner and footer links.
  const [chatThreadOpen, setChatThreadOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps]   = useState(false);
  const [applicationsKey, setApplicationsKey] = useState(0);
  const [applicationsArchiveFilter, setApplicationsArchiveFilter] = useState('active');
  const [highlightAppId, setHighlightAppId] = useState(null);
  // Scroll-to-and-ring targets on the Apprenticeships and Jobs tabs (an apprenticeship status
  // notification, or a "View" from a rejected card's Explore other options).
  const [highlightApprId, setHighlightApprId] = useState(null);
  const [highlightJobId, setHighlightJobId]   = useState(null);

  // My Profile tab
  const [profileForm, setProfileForm]       = useState(null);
  const [profileSaveMsg, setProfileSaveMsg] = useState('');
  // Tracks whether profileSaveMsg is an error independently of its (translated) text --
  // color used to key off `.startsWith('Error')`, which breaks once the message is
  // rendered in Spanish (same bug class as regionOf() from Phase 1A).
  const [profileSaveMsgIsError, setProfileSaveMsgIsError] = useState(false);

  // Experience tab
  const [experience, setExperience]   = useState([]);
  const [loadingExp, setLoadingExp]   = useState(false);
  const [showExpForm, setShowExpForm] = useState(false);
  const [expForm, setExpForm]         = useState({ company: '', role: '', start_year: '', end_year: '', current: false, description: '' });

  // Certifications
  const [certifications, setCertifications] = useState([]);
  const [certBanners, setCertBanners]       = useState([]);
  const [showCertForm, setShowCertForm]     = useState(false);
  const [certForm, setCertForm]             = useState({ certification_name: '', issuing_organization: '', issue_date: '', expiry_date: '', certificate_number: '' });

  // Licenses
  const [licenses, setLicenses]           = useState([]);
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [licenseForm, setLicenseForm]     = useState({ license_name: '', license_number: '', state: '', issue_date: '', expiry_date: '' });

  // Portfolio
  const [portfolio, setPortfolio]           = useState([]);
  const [showPortfolioForm, setShowPortfolioForm] = useState(false);
  const [portfolioForm, setPortfolioForm]   = useState({ title: '', description: '', videoUrl: '' });
  const [portfolioLinkMode, setPortfolioLinkMode] = useState(false);
  const portfolioVideoInputRef              = useRef(null);
  const [portfolioVideoFile, setPortfolioVideoFile] = useState(null);
  const [portfolioVideoUploading, setPortfolioVideoUploading] = useState(false);
  const [portfolioVideoProgress, setPortfolioVideoProgress] = useState(0);

  // Settings tab
  const [pwForm, setPwForm] = useState({ newPw: '', confirmPw: '' });
  const [pwMsg, setPwMsg]   = useState('');
  // See profileSaveMsgIsError above -- pwMsg's color used to key off `pwMsg === 'Password
  // updated!'`, which breaks once the message renders translated.
  const [pwMsgIsSuccess, setPwMsgIsSuccess] = useState(false);
  const [emailNotif, setEmailNotif] = useState(false);

  // Profile completion
  const [hasCerts, setHasCerts]       = useState(false);
  const [hasLicenses, setHasLicenses] = useState(false);

  // Photo upload
  const photoInputRef                       = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMsg, setPhotoMsg]             = useState('');
  // See profileSaveMsgIsError above -- same fix, same reason.
  const [photoMsgIsError, setPhotoMsgIsError] = useState(false);
  const [cropFile, setCropFile]             = useState(null);
  const [workerCertNames, setWorkerCertNames]   = useState([]);
  const [workerLicenseNames, setWorkerLicenseNames] = useState([]);
  const certDocInputRef                         = useRef(null);
  const [certDocFile, setCertDocFile]           = useState(null);
  const [certDocUploading, setCertDocUploading] = useState(false);
  const [certDragOver, setCertDragOver]         = useState(false);
  const [certFormMsg, setCertFormMsg]           = useState('');
  // See profileSaveMsgIsError above -- same fix, same reason.
  const [certFormMsgIsError, setCertFormMsgIsError] = useState(false);
  const licDocInputRef                          = useRef(null);
  const [licDocFile, setLicDocFile]             = useState(null);
  const [licDocUploading, setLicDocUploading]   = useState(false);
  const [licDragOver, setLicDragOver]           = useState(false);
  const [licFormMsg, setLicFormMsg]             = useState('');
  const [licFormMsgIsError, setLicFormMsgIsError] = useState(false);
  const resumeInputRef                          = useRef(null);
  const [resumeUploading, setResumeUploading]   = useState(false);
  const [resumeDragOver, setResumeDragOver]     = useState(false);
  const [resumeUrl, setResumeUrl]               = useState(null);

  // Profile views
  const [profileViewCount, setProfileViewCount] = useState(0);
  const [profileViewNotifs, setProfileViewNotifs] = useState([]);

  // Saved jobs
  const [savedJobIds, setSavedJobIds]   = useState(new Set());
  const [savedJobs, setSavedJobs]       = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  // Job search (Dashboard tab)
  const [jobSearch, setJobSearch] = useState('');

  // Offer handling (Applications tab)
  const [expandedApp, setExpandedApp]         = useState(null);
  const [expandedJobDetail, setExpandedJobDetail] = useState(null);
  const [appOffers, setAppOffers]             = useState({});
  const [offerSenders, setOfferSenders]       = useState({}); // application_id -> employer_staff { display_name, avatar_url }
  const [offerLoading, setOfferLoading]       = useState(null);
  const [seenOffers, setSeenOffers]           = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('seenOffers') || '[]')); }
    catch { return new Set(); }
  });

  // All Jobs tab
  const [allJobs, setAllJobs]                       = useState([]);
  const [allJobsLoaded, setAllJobsLoaded]           = useState(false);
  const [allJobsSearch, setAllJobsSearch]           = useState('');
  const [allJobsTradeFilter, setAllJobsTradeFilter] = useState('all');
  // Set by Explore Other Options' "See all jobs": every trade_category spelling of one occupation
  // (e.g. Electrician + Electrical), shown as a single dropdown entry while the filter is '__group'.
  const [allJobsTradeGroup, setAllJobsTradeGroup] = useState([]);
  const [allJobsTypeFilter, setAllJobsTypeFilter]   = useState('all');
  const [allJobsCityFilter, setAllJobsCityFilter]   = useState('all');

  // Dismiss job (Not interested) modal
  const [dismissJobModal, setDismissJobModal]       = useState(null); // { id, job_title }
  const [dismissReason, setDismissReason]           = useState('');
  const [dismissDetail, setDismissDetail]           = useState('');
  const [dismissSubmitting, setDismissSubmitting]   = useState(false);

  // Apprenticeships tab
  const [apprenticeships, setApprenticeships]             = useState([]);
  const [apprenticeshipsLoaded, setApprenticeshipsLoaded] = useState(false);
  const [apprenticeshipSearch, setApprenticeshipSearch]   = useState('');
  const [expandedAppr, setExpandedAppr]                   = useState(null);
  const [apprenticeshipStatusMap, setApprenticeshipStatusMap]   = useState({});
  const [unionSlugMap, setUnionSlugMap]                   = useState({});
  const [apprExamsMap, setApprExamsMap]                   = useState({}); // apprenticeship_id -> union_exams rows
  // ☆ selections for the three comparison views (job ids, application ids, apprenticeship ids)
  // and which one is open: 'jobs' | 'offers' | 'apprenticeships' | null.
  const jobCompare   = useCompareSelection();
  const offerCompare = useCompareSelection();
  const apprCompare  = useCompareSelection();
  const [openCompare, setOpenCompare] = useState(null);
  const [applyingApprenticeshipId, setApplyingApprenticeshipId] = useState(null);
  // apprenticeship id -> { kind: 'error' | 'info', text } shown under that card's Interested button.
  const [apprApplyMsg, setApprApplyMsg] = useState({});
  // Whether the worker_profiles / worker_trades lookups actually succeeded. The "finish setup"
  // banner only shows for a row that is confirmed missing, never for one that failed to load.
  const [setupRowsChecked, setSetupRowsChecked] = useState({ profile: false, trades: false });

  // Mobile layout
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [mobileJobsOpen, setMobileJobsOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [appsBadge, setAppsBadge] = useState(false);
  const [jobsBadge, setJobsBadge] = useState(false);
  const [acceptConfirmModal, setAcceptConfirmModal] = useState(null);
  const [acceptConfirmChecked, setAcceptConfirmChecked] = useState(false);
  const [acceptSignature, setAcceptSignature] = useState('');
  // Set once, synchronously, inside handleAcceptOffer -- never persisted, never re-derived from
  // application status on load, so it only ever shows immediately after accepting and can't
  // reappear on a later visit or page refresh.
  const [hireCelebration, setHireCelebration] = useState(null);

  // Latest-value refs for the realtime jobs subscription below, which subscribes
  // once per worker and must not resubscribe every time these change.
  const tradesRef = useRef(trades);
  const workerCertNamesRef = useRef(workerCertNames);
  const workerLicenseNamesRef = useRef(workerLicenseNames);
  const matchedJobStatusRef = useRef(matchedJobStatusByJobId);
  const allJobsLoadedRef = useRef(allJobsLoaded);
  const workerRef = useRef(worker);
  const profileRef = useRef(profile);
  useEffect(() => { tradesRef.current = trades; }, [trades]);
  useEffect(() => { workerCertNamesRef.current = workerCertNames; }, [workerCertNames]);
  useEffect(() => { workerLicenseNamesRef.current = workerLicenseNames; }, [workerLicenseNames]);
  useEffect(() => { matchedJobStatusRef.current = matchedJobStatusByJobId; }, [matchedJobStatusByJobId]);
  useEffect(() => { allJobsLoadedRef.current = allJobsLoaded; }, [allJobsLoaded]);
  useEffect(() => { workerRef.current = worker; }, [worker]);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // ── Connected organizations (via organization_members) -- gates who this worker can
  // request/receive a Check-in Call with. A worker with no organization_members rows
  // simply sees no Check-ins panel content, same "unknown/none never fails loudly" style
  // as the rest of this dashboard. ──
  const [connectedOrgs, setConnectedOrgs] = useState([]);
  // Same rows as connectedOrgs but carrying the membership row id, which is what the
  // "Leave organization" action in Settings updates.
  const [myMemberships, setMyMemberships] = useState([]);
  const [leavingOrgId, setLeavingOrgId] = useState(null);
  const [leaveError, setLeaveError] = useState('');
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  // Check-ins tab: which membership's "Disconnect" is showing its confirm step.
  const [confirmDisconnectId, setConfirmDisconnectId] = useState(null);
  useEffect(() => {
    if (!worker?.id) return;
    let cancelled = false;
    (async () => {
      // left_at is a real access boundary, not a label: a departed member must stop showing
      // up as a check-in counterpart, so only current memberships become connectedOrgs.
      const { data: memberRows } = await supabase
        .from('organization_members')
        .select('id, organization_id, left_at')
        .eq('worker_id', worker.id);
      const current = (memberRows || []).filter(r => !r.left_at);
      const orgIds = [...new Set(current.map(r => r.organization_id))];
      if (orgIds.length === 0) { if (!cancelled) { setConnectedOrgs([]); setMyMemberships([]); setMembershipsLoaded(true); } return; }
      // logo_url for the Check-ins header; if that column can't be read, fall back to names only.
      let { data: orgs, error: orgsError } = await supabase.from('organizations').select('id, org_name, logo_url').in('id', orgIds);
      if (orgsError) ({ data: orgs } = await supabase.from('organizations').select('id, org_name').in('id', orgIds));
      const orgById = Object.fromEntries((orgs || []).map(o => [o.id, o]));
      if (!cancelled) {
        setConnectedOrgs((orgs || []).map(o => ({ id: o.id, name: o.org_name })));
        setMyMemberships(current.map(r => ({ id: r.id, organization_id: r.organization_id, name: orgById[r.organization_id]?.org_name || '—', logo_url: orgById[r.organization_id]?.logo_url || null })));
        setMembershipsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [worker?.id]);

  // Members can leave an organization themselves -- guard_org_member_leave permits a
  // self-leave (and an admin removal) but blocks un-leaving, so this is one-way by design and
  // the confirm says so. Sets left_at rather than deleting, keeping check-in and message
  // history attributable on the organization's side.
  // skipConfirm: the Check-ins tab has its own on-page confirm step instead of window.confirm.
  const handleLeaveOrganization = async (membership, { skipConfirm = false } = {}) => {
    if (!skipConfirm && !window.confirm(t('worker.settings.leaveOrgConfirm', { name: membership.name }))) return;
    setLeavingOrgId(membership.id);
    setLeaveError('');
    const { error } = await supabase
      .from('organization_members')
      .update({ left_at: new Date().toISOString() })
      .eq('id', membership.id);
    setLeavingOrgId(null);
    if (error) { setLeaveError(error.message); return; }
    setMyMemberships(prev => prev.filter(m => m.id !== membership.id));
    setConnectedOrgs(prev => prev.filter(o => o.id !== membership.organization_id));
    setConfirmDisconnectId(null);
  };

  // ── Load initial data ──────────────────────────────────────
  useEffect(() => {
    const loadWorkerData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const userId = session.user.id;
      const [workerRes, profileRes, tradesRes, certsRes, licensesRes] = await Promise.all([
        supabase.from('workers').select('*').eq('id', userId).single(),
        // maybeSingle, not single: "no row" is a real, expected state (an org-invited worker who
        // hasn't finished signup) and must be told apart from a failed load for the setup banner.
        supabase.from('worker_profiles').select('*').eq('id', userId).maybeSingle(),
        supabase.from('worker_trades').select('*').eq('worker_id', userId).order('id').limit(1).maybeSingle(),
        supabase.from('worker_certifications').select('id, certification_name').eq('worker_id', userId),
        supabase.from('worker_licenses').select('id, license_name').eq('worker_id', userId),
      ]);

      setWorker(workerRes.data);
      setResumeUrl(workerRes.data?.resume_url || null);
      setProfile(profileRes.data);
      setTrades(tradesRes.data);
      setSetupRowsChecked({ profile: !profileRes.error, trades: !tradesRes.error });
      const certNamesArr = (certsRes.data || []).map(c => c.certification_name || '').filter(Boolean);
      setWorkerCertNames(certNamesArr);
      setHasCerts((certsRes.data || []).length > 0);
      const licenseNamesArr = (licensesRes.data || []).map(l => l.license_name || '').filter(Boolean);
      setWorkerLicenseNames(licenseNamesArr);
      setHasLicenses((licensesRes.data || []).length > 0);

      const workerTrade = tradesRes.data?.trade;
      const todayISO = new Date().toISOString().slice(0, 10);
      // No trade set (e.g. an org-invited worker who never went through WorkerStep3) means
      // Matched Jobs has nothing to match against -- zero results, consistent with
      // computeMatchTier's own treatment of the same case, not "every trade."
      let jobsData = [];
      if (workerTrade) {
        // Featured jobs are fetched separately, with no limit/recency bound, and merged in --
        // the 20-most-recent query alone could otherwise silently exclude an older featured
        // job before "pin to top" ever gets a chance to sort it into view.
        const [{ data: recentJobs }, { data: featuredJobs }] = await Promise.all([
          supabase
            .from('jobs')
            .select('*')
            .eq('status', 'active')
            .or(`expires_at.is.null,expires_at.gte.${todayISO}`)
            .eq('trade_category', workerTrade)
            .order('created_at', { ascending: false })
            .limit(20),
          supabase
            .from('jobs')
            .select('*')
            .eq('status', 'active')
            .or(`expires_at.is.null,expires_at.gte.${todayISO}`)
            .eq('trade_category', workerTrade)
            .eq('featured', true),
        ]);
        const jobsById = new Map();
        (featuredJobs || []).forEach(j => jobsById.set(j.id, j));
        (recentJobs || []).forEach(j => jobsById.set(j.id, j));
        jobsData = Array.from(jobsById.values()).sort((a, b) => {
          if (!!a.featured !== !!b.featured) return a.featured ? -1 : 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
      }

      // Fetch the worker's own applications (batched, single query) so Matched Jobs
      // can exclude postings already hired/rejected and badge the rest by status.
      const { data: myApplications } = await supabase
        .from('applications')
        .select('job_id, status')
        .eq('worker_id', userId);
      const applicationStatusByJobId = {};
      (myApplications || []).forEach(a => { applicationStatusByJobId[a.job_id] = a.status; });

      const { data: myDismissals } = await supabase
        .from('job_dismissals')
        .select('job_id')
        .eq('worker_id', userId);
      const dismissedJobIdSet = new Set((myDismissals || []).map(d => d.job_id));

      // Filter to only jobs where the worker has all required certifications and licenses
      const certSet = new Set(certNamesArr.map(n => n.toLowerCase().trim()));
      const licenseSet = new Set(licenseNamesArr.map(n => n.toLowerCase().trim()));
      const matchedJobs = (jobsData || []).filter(job => {
        if (dismissedJobIdSet.has(job.id)) return false;
        const appStatus = applicationStatusByJobId[job.id];
        if (appStatus === 'hired' || appStatus === 'rejected') return false;
        const requiredCerts = parseCertsRequired(job.certifications_required);
        const requiredLicenses = parseCertsRequired(job.licenses_required);
        return requiredCerts.every(c => certSet.has(c.toLowerCase().trim()))
          && requiredLicenses.every(l => licenseSet.has(l.toLowerCase().trim()))
          && matchesScheduleFilter(job, tradesRes.data?.availability)
          && matchesRadiusFilter(job, workerRes.data?.zip_code, profileRes.data?.commute_radius_miles);
      });
      setJobs(matchedJobs);
      setMatchedJobStatusByJobId(applicationStatusByJobId);

      // Jobs badge: new matched jobs since last visit
      const lastJobsVisit = localStorage.getItem('lastJobsVisit') || '0';
      if (matchedJobs.some(j => j.created_at > lastJobsVisit)) setJobsBadge(true);

      // Applications badge: actionable statuses not yet acknowledged
      const { data: badgeApps } = await supabase
        .from('applications')
        .select('id, status')
        .eq('worker_id', userId)
        .in('status', APPS_BADGE_STATUSES);
      if (hasUnseenApps(badgeApps || [])) setAppsBadge(true);

      const { data: applicationsData } = await supabase
        .from('applications')
        .select('job_id')
        .eq('worker_id', userId);
      setAppliedJobs(applicationsData?.map(a => a.job_id) || []);

      const { data: savedData } = await supabase
        .from('saved_jobs')
        .select('job_id')
        .eq('worker_id', userId);
      setSavedJobIds(new Set((savedData || []).map(s => s.job_id)));

      // Excludes conversations this worker has hidden -- those don't appear in the
      // Messages list, so unread messages inside them would otherwise be un-clearable.
      const { data: visibleConvs } = await supabase
        .from('conversations')
        .select('id')
        .eq('worker_id', userId)
        .is('worker_hidden_at', null);
      if (visibleConvs && visibleConvs.length > 0) {
        // Badge unit: unread conversations, matching the Messages list (see countUnreadConversations).
        const unreadConvCount = await countUnreadConversations(supabase, visibleConvs.map(c => c.id), 'worker');
        setUnreadCount(unreadConvCount);
        setMobileMessageBadge(unreadConvCount);
      } else {
        setUnreadCount(0);
        setMobileMessageBadge(0);
      }

      const { count: viewCount } = await supabase
        .from('profile_views')
        .select('*', { count: 'exact', head: true })
        .eq('worker_id', userId);
      setProfileViewCount(viewCount || 0);

      const { data: viewsData } = await supabase
        .from('profile_views')
        .select('id, employer_id, viewed_at')
        .eq('worker_id', userId)
        .order('viewed_at', { ascending: false })
        .limit(5);
      if (viewsData && viewsData.length > 0) {
        const { data: dismissedData } = await supabase
          .from('dismissed_notifications')
          .select('profile_view_id')
          .eq('worker_id', userId);
        const dismissedIds = new Set((dismissedData || []).map(d => d.profile_view_id));
        const undismissed = viewsData.filter(v => !dismissedIds.has(v.id));
        if (undismissed.length > 0) {
          const employerIds = undismissed.map(v => v.employer_id);
          const { data: employersData } = await supabase
            .from('employers')
            .select('id, company_name')
            .in('id', employerIds);
          const notifs = undismissed.map(v => ({
            ...v,
            company_name: employersData?.find(e => e.id === v.employer_id)?.company_name || 'An employer',
          }));
          setProfileViewNotifs(notifs);
        }
      }

      setLoading(false);
    };
    loadWorkerData();
  }, [navigate, refreshKey]); // eslint-disable-line

  // ── Sidebar: up to 2 soonest upcoming interviews, read straight from the interviews table ──
  // Interviews live independently of conversation state, so hiding/deleting a conversation
  // no longer affects this. Depends on refreshKey so the realtime `interviews` subscription
  // below (new interview, or a status change like accept/decline) forces a recompute.
  useEffect(() => {
    if (!worker?.id) return;
    const loadUpcomingInterviews = async () => {
      const { data: rows } = await supabase
        .from('interviews')
        .select('id, conversation_id, employer_id, union_id, interview_at, meeting_type, meeting_link, status')
        .eq('worker_id', worker.id)
        .not('status', 'in', '(cancelled,completed)')
        .gte('interview_at', new Date().toISOString())
        .order('interview_at', { ascending: true })
        .limit(2);
      if (!rows?.length) { setUpcomingInterviews([]); return; }

      const mapped = await Promise.all(rows.map(async (upcoming) => {
        let otherPartyName = '';
        if (upcoming.employer_id) {
          const { data: emp } = await supabase.from('employers').select('company_name').eq('id', upcoming.employer_id).single();
          otherPartyName = emp?.company_name || '';
        } else if (upcoming.union_id) {
          const { data: un } = await supabase.from('union_accounts').select('union_name').eq('id', upcoming.union_id).single();
          otherPartyName = un?.union_name || '';
        }
        return {
          id: upcoming.id,
          conversationId: upcoming.conversation_id,
          otherPartyName,
          interviewAt: upcoming.interview_at,
          meetingType: upcoming.meeting_type,
          meetingLink: upcoming.meeting_link,
          status: upcoming.status,
        };
      }));
      setUpcomingInterviews(mapped);
    };
    loadUpcomingInterviews();
  }, [worker?.id, refreshKey]); // eslint-disable-line

  // ── Live countdown labels: re-render the upcoming-interview card(s) every 30s so the
  // "Interview in X min" / "Starting now" labels stay accurate without a page reload ──
  useEffect(() => {
    if (!upcomingInterviews.length) return;
    const tick = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tick);
  }, [upcomingInterviews.length]);

  // ── Clicking the upcoming-interview card joins the meeting directly when a link
  // exists; otherwise falls back to opening the associated conversation ──────────
  const openUpcomingInterview = (interview) => {
    if (interview.meetingLink) {
      if (interview.meetingType === 'daily') {
        window.open(`/interview?interviewId=${interview.id}`, '_blank');
      } else {
        window.open(interview.meetingLink, '_blank', 'noopener,noreferrer');
      }
    } else {
      setActiveTab('Messages');
      setOpenConversationId(interview.conversationId);
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; }
      input::placeholder { color: rgba(255,255,255,0.28); }
      select option { background-color: ${NAVY_MID}; color: ${WHITE}; }
      .company-link:hover { text-decoration: underline; }
      .dash-tab-bar::-webkit-scrollbar { display: none; }
      @keyframes pulse-border {
        0%   { box-shadow: 0 0 0 0 rgba(245, 166, 35, 0.4); }
        70%  { box-shadow: 0 0 0 8px rgba(245, 166, 35, 0); }
        100% { box-shadow: 0 0 0 0 rgba(245, 166, 35, 0); }
      }
      @media (max-width: 767px) {
        .dash-sidebar { display: none !important; }
        .dash-main { margin-left: 0 !important; padding-left: 16px !important; padding-right: 16px !important; padding-bottom: calc(68px + env(safe-area-inset-bottom)) !important; }
        .dash-bottom-nav { display: flex !important; }
        .dash-nav-badge { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Lazy-load Applications ────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'Applications' || !worker) return;
    const loadApplications = async () => {
      setApplications([]);
      setLoadingApps(true);
      const { data: appsData } = await supabase
        .from('applications')
        .select('id, job_id, employer_id, status, created_at, worker_archived_at')
        .eq('worker_id', worker.id)
        .is('worker_hidden_at', null)
        .order('created_at', { ascending: false });

      if (!appsData || appsData.length === 0) {
        setApplications([]);
        setLoadingApps(false);
        return;
      }

      // offer_sent first, then most recent by created_at
      appsData.sort((a, b) => {
        if (a.status === 'offer_sent' && b.status !== 'offer_sent') return -1;
        if (b.status === 'offer_sent' && a.status !== 'offer_sent') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      // Filter out null job_ids (orphaned applications whose job was removed) —
      // a null in this list would otherwise break the batched .in() lookup for
      // every application, not just the orphaned one.
      const jobIds        = [...new Set(appsData.map(a => a.job_id).filter(Boolean))];
      const empIds       = [...new Set(appsData.map(a => a.employer_id).filter(Boolean))];
      const offerSentIds = appsData.filter(a => a.status === 'offer_sent').map(a => a.id);

      const [{ data: jobsData }, { data: employersData }, { data: offerRows }, { data: anyOfferRows }] = await Promise.all([
        supabase.from('jobs').select('id, job_title, company_name, city, state, salary_min, salary_max, job_type, job_description, trade_category, min_years_experience, union_status, sign_on_bonus_amount, per_diem, per_diem_amount, shift, benefits, travel_required, travel_details, urgently_hiring, willing_to_train').in('id', jobIds),
        empIds.length > 0
          ? supabase.from('employers').select('id, company_name, logo_url').in('id', empIds)
          : Promise.resolve({ data: [] }),
        offerSentIds.length > 0
          ? supabase.from('job_offers').select('id, application_id').in('application_id', offerSentIds).order('created_at', { ascending: false })
          : Promise.resolve({ data: [] }),
        // Every application that has received an offer in any state (pending, accepted or
        // declined) -- what the offer comparison's ☆ is shown on.
        supabase.from('job_offers').select('application_id').eq('worker_id', worker.id),
      ]);
      const appsWithOffer = new Set((anyOfferRows || []).map(r => r.application_id));

      const jobMap      = Object.fromEntries((jobsData || []).map(j => [j.id, j]));
      const employerMap = Object.fromEntries((employersData || []).map(e => [e.id, e]));
      const offerMetaMap = {};
      (offerRows || []).forEach(row => {
        if (!offerMetaMap[row.application_id]) offerMetaMap[row.application_id] = { latestOfferId: row.id, offerCount: 0 };
        offerMetaMap[row.application_id].offerCount++;
      });

      setApplications(appsData.map(app => ({
        ...app,
        job:           jobMap[app.job_id],
        employer:      employerMap[app.employer_id],
        latestOfferId: offerMetaMap[app.id]?.latestOfferId || null,
        offerCount:    offerMetaMap[app.id]?.offerCount || 0,
        hasReceivedOffer: appsWithOffer.has(app.id),
      })));
      setLoadingApps(false);
    };
    loadApplications();
  }, [activeTab, applicationsKey, worker]); // eslint-disable-line

  // ── Fetch employer details when a job is expanded ─────────
  useEffect(() => {
    if (!expandedJob) return;
    const job = [...jobs, ...allJobs].find(j => j.id === expandedJob);
    const empId = job?.employer_id;
    if (!empId || employerDetailsCache[empId]) return;
    supabase
      .from('employers')
      .select('about, website, year_founded, industry, union_status, typical_benefits')
      .eq('id', empId)
      .single()
      .then(({ data }) => {
        if (data) setEmployerDetailsCache(prev => ({ ...prev, [empId]: data }));
      });
  }, [expandedJob]); // eslint-disable-line

  // ── Clear badges when tabs are visited ──────────────────────
  useEffect(() => {
    if (activeTab === 'Messages') { setUnreadCount(0); setMobileMessageBadge(0); }
    if (activeTab === 'Jobs') {
      localStorage.setItem('lastJobsVisit', new Date().toISOString());
      setJobsBadge(false);
    }
    if (activeTab === 'Applications') setAppsBadge(false);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'Applications' || loadingApps || applications.length === 0) return;
    const keys = applications
      .filter(a => APPS_BADGE_STATUSES.includes(a.status))
      .map(appSeenKey);
    try { localStorage.setItem(SEEN_APPS_KEY, JSON.stringify(keys)); } catch (_) {}
  }, [applications, activeTab, loadingApps]);

  // Scroll to and briefly highlight the application a notification deep-linked to.
  // Also flips the active/archived filter so the target is actually visible, and
  // (for an offer notification) opens the offer details, since that's what the
  // notification is actually about.
  useEffect(() => {
    if (!highlightAppId || activeTab !== 'Applications' || loadingApps) return;
    const app = applications.find(a => a.id === highlightAppId);
    // applications loads lazily per tab-visit (unlike Employer/Union's candidates, which
    // loads unconditionally on mount) -- this effect can fire once against a still-empty
    // array right after navigating here, before the real load kicks in. Just wait for the
    // next update rather than giving up, or it clears itself before data ever arrives.
    if (!app) return;

    const wantsArchived = !!app.worker_archived_at;
    if (wantsArchived && applicationsArchiveFilter !== 'archived') { setApplicationsArchiveFilter('archived'); return; }
    if (!wantsArchived && applicationsArchiveFilter === 'archived') { setApplicationsArchiveFilter('active'); return; }

    const id = highlightAppId;
    requestAnimationFrame(() => {
      document.getElementById(`application-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    if (app.status === 'offer_sent') {
      setExpandedApp(id);
      handleLoadOffer(id, app.status);
    }
    const t = setTimeout(() => setHighlightAppId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightAppId, activeTab, loadingApps, applications, applicationsArchiveFilter]); // eslint-disable-line

  useEffect(() => {
    if (!highlightApprId || activeTab !== 'Apprenticeships' || !apprenticeshipsLoaded) return;
    requestAnimationFrame(() => {
      document.getElementById(`apprenticeship-${highlightApprId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const t = setTimeout(() => setHighlightApprId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightApprId, activeTab, apprenticeshipsLoaded]);

  useEffect(() => {
    if (!highlightJobId || activeTab !== 'Jobs' || !allJobsLoaded) return;
    setExpandedJob(highlightJobId);
    requestAnimationFrame(() => {
      document.getElementById(`job-${highlightJobId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const t = setTimeout(() => setHighlightJobId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightJobId, activeTab, allJobsLoaded]);

  // ── Lazy-load All Jobs tab ────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'Jobs' || allJobsLoaded || !worker) return;
    (async () => {
      const todayISO = new Date().toISOString().slice(0, 10);
      const [{ data: jobsData }, { data: myDismissals }] = await Promise.all([
        supabase.from('jobs').select('*').eq('status', 'active').or(`expires_at.is.null,expires_at.gte.${todayISO}`).order('featured', { ascending: false }).order('created_at', { ascending: false }),
        supabase.from('job_dismissals').select('job_id').eq('worker_id', worker.id),
      ]);
      const dismissedJobIdSet = new Set((myDismissals || []).map(d => d.job_id));
      setAllJobs((jobsData || []).filter(j => !dismissedJobIdSet.has(j.id)));
      setAllJobsLoaded(true);
    })();
  }, [activeTab, allJobsLoaded, worker]); // eslint-disable-line

  // ── Initialize profile form when worker loads ─────────────
  useEffect(() => {
    if (!worker) return;
    setProfileForm({
      firstName:    worker.first_name      || '',
      lastName:     worker.last_name       || '',
      phone:        worker.phone           || '',
      zip:          worker.zip_code        || '',
      city:         profile?.city          || '',
      state:        profile?.state         || '',
      trade:        trades?.trade          || '',
      availability: trades?.availability   || '',
      unionStatus:  trades?.union_interest  || '',
      commuteRadius: profile?.commute_radius_miles ? String(profile.commute_radius_miles) : '',
    });
    setEmailNotif(worker.email_notifications || false);
  }, [worker, trades, profile?.city, profile?.state, profile?.commute_radius_miles]);

  // ── Lazy-load Experience, Certifications, Licenses, Portfolio ─
  useEffect(() => {
    if (activeTab !== 'Experience' || !worker) return;
    setLoadingExp(true);
    const loadAll = async () => {
      const [expRes, certsRes, licensesRes, portfolioRes] = await Promise.all([
        supabase.from('worker_experience').select('*').eq('worker_id', worker.id).order('start_year', { ascending: false }),
        supabase.from('worker_certifications').select('*').eq('worker_id', worker.id).order('created_at', { ascending: false }),
        supabase.from('worker_licenses').select('*').eq('worker_id', worker.id).order('created_at', { ascending: false }),
        supabase.from('worker_portfolio').select('*').eq('worker_id', worker.id).order('created_at', { ascending: false }),
      ]);
      setExperience(expRes.data || []);
      const certsData = certsRes.data || [];
      setCertifications(certsData);
      setLicenses(licensesRes.data || []);
      setPortfolio(portfolioRes.data || []);
      setLoadingExp(false);

      // Build cert status banners for recently verified/rejected certs
      const dismissedKey = `certBannersDismissed_${worker.id}`;
      const dismissed = new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]'));
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const banners = certsData
        .filter(c => {
          if (!c.verification_status || c.verification_status === 'pending' || c.verification_status === 'unverified') return false;
          if (dismissed.has(c.id)) return false;
          const updatedAt = c.verified_at || c.updated_at || c.created_at;
          return updatedAt && new Date(updatedAt).getTime() > sevenDaysAgo;
        })
        .map(c => ({ id: c.id, status: c.verification_status, name: c.certification_name }));
      setCertBanners(banners);
    };
    loadAll();
  }, [activeTab, worker]); // eslint-disable-line

  // ── Lazy-load Saved Jobs ────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'Saved Jobs' || !worker) return;
    const loadSaved = async () => {
      setLoadingSaved(true);
      const { data: savedData } = await supabase
        .from('saved_jobs')
        .select('job_id')
        .eq('worker_id', worker.id);
      const jobIds = (savedData || []).map(s => s.job_id);
      if (jobIds.length === 0) {
        setSavedJobs([]);
        setLoadingSaved(false);
        return;
      }
      const { data: jobsData } = await supabase
        .from('jobs')
        .select('*')
        .in('id', jobIds);
      setSavedJobs(jobsData || []);
      setLoadingSaved(false);
    };
    loadSaved();
  }, [activeTab, worker]); // eslint-disable-line

  // ── Lazy-load Apprenticeships ────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'Apprenticeships' || !worker) return;
    const loadApprenticeships = async () => {
      // Open postings (active and not archived) are the browsable list. The worker's own
      // applications are a separate question: a posting they applied to can since have been
      // closed -- which hides the apprenticeships row from them entirely under RLS -- or
      // archived. my_applied_apprenticeship_postings() returns title/union/trade/status for
      // every posting they applied to, whatever its state, so those application cards (and
      // Explore Other Options on a rejected one) keep working after the posting stops being open.
      const [openRes, appsRes, appliedRes] = await Promise.all([
        supabase.from('apprenticeships').select('*').eq('status', 'active').is('archived_at', null).order('created_at', { ascending: false }),
        supabase.from('apprenticeship_applications').select('apprenticeship_id, status').eq('worker_id', worker.id),
        supabase.rpc('my_applied_apprenticeship_postings'),
      ]);
      const open = openRes.data || [];
      const apps = appsRes.data || [];
      const appliedById = Object.fromEntries(((!appliedRes.error && appliedRes.data) || []).map(r => [r.id, r]));
      const openIds = new Set(open.map(a => a.id));

      // Applied + still open: the full row, with the RPC as the source for title/union/trade.
      const openCards = open.map(a => {
        const r = appliedById[a.id];
        return r ? { ...a, title: r.title, union_id: r.union_id, union_name: r.union_name, trade: r.trade } : a;
      });
      // Applied + no longer open: a reduced card from the RPC's fields alone. If the RPC failed
      // or has no row for an application, the card still renders with a generic title rather
      // than vanishing (detailsUnavailable).
      const closedCards = apps.filter(app => !openIds.has(app.apprenticeship_id)).map(app => {
        const r = appliedById[app.apprenticeship_id];
        return r
          ? { id: r.id, title: r.title, union_id: r.union_id, union_name: r.union_name, trade: r.trade, status: r.status, noLongerOpen: true }
          : { id: app.apprenticeship_id, noLongerOpen: true, detailsUnavailable: true };
      });
      const data = [...openCards, ...closedCards];
      setApprenticeships(data);
      setApprenticeshipStatusMap(Object.fromEntries(apps.map(a => [a.apprenticeship_id, a.status])));
      setApprenticeshipsLoaded(true);

      const unionIds = [...new Set(data.map(a => a.union_id).filter(Boolean))];
      const [{ data: unions }, { data: examLinks }] = await Promise.all([
        // apprenticeships.union_id is a union_accounts id, and the public page resolves
        // /unions/:slug from union_accounts.slug -- this used to look the ids up in the
        // separate `unions` directory table, where they never matched, so every
        // "View union profile" link fell back to the generic /union page.
        unionIds.length ? supabase.from('union_accounts').select('id, slug').in('id', unionIds) : { data: [] },
        open.length
          ? supabase.from('apprenticeship_exam_requirements')
              .select('apprenticeship_id, union_exams(id, name, description, next_test_date, registration_deadline, location, passing_score, exam_fee, study_guide_url, scheduled_individually, contact_name, contact_title, contact_phone, contact_email, union_exam_sessions(test_date, registration_deadline))')
              .in('apprenticeship_id', open.map(a => a.id))
          : { data: [] },
      ]);
      setUnionSlugMap(Object.fromEntries((unions || []).filter(u => u.slug).map(u => [u.id, u.slug])));
      const examsByAppr = {};
      (examLinks || []).forEach(link => {
        if (!link.union_exams) return;
        // The embed arrives under the table name; the display helpers read `sessions`.
        const { union_exam_sessions: sessions, ...exam } = link.union_exams;
        (examsByAppr[link.apprenticeship_id] ||= []).push({ ...exam, sessions: sessions || [] });
      });
      setApprExamsMap(examsByAppr);
    };
    loadApprenticeships();
  }, [activeTab, worker]); // eslint-disable-line

  const handleApplyApprenticeship = async (appr) => {
    if (apprenticeshipStatusMap[appr.id] || applyingApprenticeshipId === appr.id) return;
    setApplyingApprenticeshipId(appr.id);
    const showMsg = (kind, key) => setApprApplyMsg(prev => ({ ...prev, [appr.id]: { kind, text: t(key) } }));
    setApprApplyMsg(prev => { const { [appr.id]: _, ...rest } = prev; return rest; });
    // Exactly the four columns the insert policy allows; union_id is the posting's own union.
    // The application only counts once the created row comes back.
    const { data: created, error } = await supabase.from('apprenticeship_applications').insert({
      worker_id: worker.id,
      union_id: appr.union_id,
      apprenticeship_id: appr.id,
      status: 'pending',
    }).select('id');
    if (error?.code === '23505') {
      // unique (apprenticeship_id, worker_id): already applied, e.g. from the public union
      // page in another tab. Show the application's real status rather than an error.
      const { data: existing } = await supabase.from('apprenticeship_applications')
        .select('status').eq('apprenticeship_id', appr.id).eq('worker_id', worker.id).maybeSingle();
      setApprenticeshipStatusMap(prev => ({ ...prev, [appr.id]: existing?.status || 'pending' }));
      showMsg('info', 'worker.dashboard.apprenticeships.alreadyApplied');
    } else if (error || created?.length !== 1) {
      showMsg('error', 'worker.dashboard.apprenticeships.applyFailed');
    } else {
      setApprenticeshipStatusMap(prev => ({ ...prev, [appr.id]: 'pending' }));
      // Writes the in-app rows for every active staff seat and emails them. This replaces a
      // direct notifications insert that only ever reached the owner's id -- and the employer
      // equivalent has always emailed on a new applicant, where the union side was silent.
      const applicantName = (`${worker?.first_name || ''} ${worker?.last_name || ''}`).trim() || 'A worker';
      notifyUnionStaff(supabase, {
        unionId:    appr.union_id,
        inAppType:  'new_interest',
        emailType:  'apprenticeship_interest',
        message:    `${applicantName} expressed interest in ${appr.title}`,
        linkTo:     JSON.stringify({ tab: 'Candidates', worker_id: worker.id }),
        worker_name:  applicantName,
        worker_trade: trades?.trade || 'Trades worker',
        apprenticeship_title: appr.title,
      });
    }
    setApplyingApprenticeshipId(null);
  };

  const getUnionProfileUrl = (appr) => {
    const slug = unionSlugMap[appr.union_id];
    return slug ? `/unions/${slug}` : '/union';
  };

  // ── Realtime subscriptions ────────────────────────────────
  useEffect(() => {
    if (!worker?.id) return;

    const recomputeAppsBadge = async () => {
      const { data: badgeApps } = await supabase
        .from('applications')
        .select('id, status')
        .eq('worker_id', worker.id)
        .in('status', APPS_BADGE_STATUSES);
      if (hasUnseenApps(badgeApps || [])) setAppsBadge(true);
    };

    // A dropped realtime connection (network blip, backgrounded tab, mobile network
    // handoff) otherwise fails silently forever with no reconnect, leaving these two
    // channels stale until a manual reload. Resubscribe with a fresh channel name on
    // CHANNEL_ERROR/TIMED_OUT instead of leaving it dead.
    let cancelled = false;
    const subscribeWithRetry = (buildChannel, label) => {
      let current = null;
      const start = () => {
        current = buildChannel();
        current.subscribe((status) => {
          if (cancelled) return;
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[realtime] ${label} channel ${status.toLowerCase()}, resubscribing…`);
            supabase.removeChannel(current);
            setTimeout(start, 2000);
          }
        });
      };
      start();
      return { remove: () => { if (current) supabase.removeChannel(current); } };
    };

    const appsChannel = subscribeWithRetry(() => supabase
      .channel(`applications:worker:${worker.id}:${Date.now()}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'applications', filter: `worker_id=eq.${worker.id}` },
        () => { setApplicationsKey(k => k + 1); recomputeAppsBadge(); }
      ), 'applications');

    const offersChannel = subscribeWithRetry(() => supabase
      .channel(`job_offers:worker:${worker.id}:${Date.now()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'job_offers', filter: `worker_id=eq.${worker.id}` },
        () => { setApplicationsKey(k => k + 1); recomputeAppsBadge(); }
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'job_offers', filter: `worker_id=eq.${worker.id}` },
        () => { setApplicationsKey(k => k + 1); recomputeAppsBadge(); }
      ), 'job_offers');

    // New/changed jobs merged directly into state (no full reload) so Matched Jobs
    // and All Jobs reflect employer postings live. Not filtered server-side by trade,
    // since All Jobs spans every trade — filtering happens client-side per list below.
    const upsertJobIntoView = (row) => {
      // Mirrors the initial fetch queries' `.or(expires_at.is.null,expires_at.gte.today)` --
      // without this, a realtime UPDATE on an already-expired-but-still-'active' job (e.g. an
      // employer edits an unrelated field) would silently reinsert it into a worker's open tab.
      const todayISO = new Date().toISOString().slice(0, 10);
      const isActive = row.status === 'active' && (!row.expires_at || row.expires_at >= todayISO);

      setJobs(prev => {
        const exists = prev.some(j => j.id === row.id);
        const workerTrade = tradesRef.current?.trade;
        // No trade set means nothing can match (consistent with computeMatchTier and the
        // initial Matched Jobs fetch above) -- not "every trade."
        const tradeOk = !!workerTrade && row.trade_category === workerTrade;
        const required = parseCertsRequired(row.certifications_required);
        const certSet = new Set(workerCertNamesRef.current.map(n => n.toLowerCase().trim()));
        const certsOk = required.every(c => certSet.has(c.toLowerCase().trim()));
        const requiredLicenses = parseCertsRequired(row.licenses_required);
        const licenseSet = new Set(workerLicenseNamesRef.current.map(n => n.toLowerCase().trim()));
        const licensesOk = requiredLicenses.every(l => licenseSet.has(l.toLowerCase().trim()));
        const scheduleOk = matchesScheduleFilter(row, tradesRef.current?.availability);
        const radiusOk = matchesRadiusFilter(row, workerRef.current?.zip_code, profileRef.current?.commute_radius_miles);
        const appStatus = matchedJobStatusRef.current[row.id];
        const matches = isActive && tradeOk && certsOk && licensesOk && scheduleOk && radiusOk && appStatus !== 'hired' && appStatus !== 'rejected';
        if (!matches) return exists ? prev.filter(j => j.id !== row.id) : prev;
        return exists ? prev.map(j => (j.id === row.id ? { ...j, ...row } : j)) : [row, ...prev];
      });

      setAllJobs(prev => {
        if (!allJobsLoadedRef.current) return prev;
        const exists = prev.some(j => j.id === row.id);
        if (!isActive) return exists ? prev.filter(j => j.id !== row.id) : prev;
        return exists ? prev.map(j => (j.id === row.id ? { ...j, ...row } : j)) : [row, ...prev];
      });
    };
    const jobsChannel = supabase
      .channel(`jobs:worker:${worker.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'jobs' },
        (payload) => upsertJobIntoView(payload.new)
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'jobs' },
        (payload) => upsertJobIntoView(payload.new)
      )
      .subscribe();

    const apprAppsChannel = supabase
      .channel(`apprenticeship_applications:worker:${worker.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'apprenticeship_applications', filter: `worker_id=eq.${worker.id}` },
        (payload) => setApprenticeshipStatusMap(prev => ({ ...prev, [payload.new.apprenticeship_id]: payload.new.status }))
      )
      .subscribe();

    // Jobs leaving 'active' status can't be caught via postgres_changes: Realtime evaluates
    // the worker's SELECT RLS policy against the row's NEW state, and workers can't read
    // non-active jobs, so the UPDATE event is silently dropped. A DB trigger broadcasts a
    // plain (non-RLS-gated) message here instead when a job's status changes away from active.
    const jobsStatusChannel = supabase
      .channel('jobs-status')
      .on('broadcast', { event: 'job_closed' }, ({ payload }) => {
        const jobId = payload?.job_id;
        if (!jobId) return;
        setJobs(prev => prev.filter(j => j.id !== jobId));
        setAllJobs(prev => prev.filter(j => j.id !== jobId));
      })
      .subscribe();

    // Catches a conversation being hidden/unhidden, which affects the unread-message count.
    const convsChannel = supabase
      .channel(`conversations:worker:${worker.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `worker_id=eq.${worker.id}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();

    // Interviews live outside conversation state entirely, so a new interview or a
    // status change (accept/decline) needs its own subscription to recompute the card.
    const interviewsChannel = supabase
      .channel(`interviews:worker:${worker.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interviews', filter: `worker_id=eq.${worker.id}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();

    return () => {
      cancelled = true;
      appsChannel.remove();
      offersChannel.remove();
      supabase.removeChannel(jobsChannel);
      supabase.removeChannel(apprAppsChannel);
      supabase.removeChannel(jobsStatusChannel);
      supabase.removeChannel(convsChannel);
      supabase.removeChannel(interviewsChannel);
    };
  }, [worker?.id]); // eslint-disable-line

  if (loading) return (
    <div style={{ background: NAVY, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: AMBER, fontFamily: FONT_BODY, fontSize: '16px' }}>{t('worker.dashboard.loadingDashboard')}</p>
    </div>
  );

  if (!worker) return (
    <div style={{ background: NAVY, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: WHITE, fontFamily: FONT_BODY, fontSize: '16px' }}>
        Profile incomplete —{' '}
        <span onClick={() => navigate('/worker/signup/personal')} style={{ color: AMBER, fontWeight: 700, cursor: 'pointer' }}>
          Complete your profile →
        </span>
      </p>
    </div>
  );

  const initials  = `${worker?.first_name?.[0] || ''}${worker?.last_name?.[0] || ''}`;
  const fullName  = `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim();
  const tradeLabel = [trades?.trade, trades?.availability].filter(Boolean).join(' · ');

  const completionItems = [
    { done: !!(worker?.first_name && worker?.last_name), label: t('worker.dashboard.completionItems.firstLastName'),   tab: 'My Profile' },
    { done: !!worker?.phone,                             label: t('worker.dashboard.completionItems.phoneNumber'),     tab: 'My Profile' },
    { done: !!(profile?.city && profile?.state),         label: t('worker.dashboard.completionItems.cityState'),       tab: 'My Profile' },
    { done: !!trades?.trade,                             label: t('worker.dashboard.completionItems.tradeItem'),       tab: 'My Profile' },
    { done: !!profile?.profile_photo_url,                label: t('worker.dashboard.completionItems.profilePhotoItem'), tab: 'My Profile' },
    { done: !!profile?.bio,                              label: t('worker.dashboard.completionItems.bioItem'),         tab: 'My Profile' },
    { done: hasCerts,                                    label: t('worker.dashboard.completionItems.certificationsItem'), tab: 'Experience' },
    { done: hasLicenses,                                 label: t('worker.dashboard.completionItems.licensesItem'),    tab: 'Experience' },
  ];
  const completedCount = completionItems.filter(i => i.done).length;
  const profilePct     = Math.round((completedCount / 8) * 100 / 25) * 25;
  const missingItems   = completionItems.filter(i => !i.done);

  // ── Handlers ──────────────────────────────────────────────
  const handleInterested = async (job, e) => {
    e.stopPropagation();
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    if (appliedJobs.includes(job.id)) {
      // Hard backstop regardless of which surface this is called from (Jobs tab, Saved
      // Jobs tab, Matched Jobs) -- a hired (or fell-through) application is a real record,
      // never something this generic "remove interest" toggle should be able to delete.
      const myAppStatus = matchedJobStatusByJobId[job.id];
      if (myAppStatus === 'hired' || myAppStatus === 'hire_fell_through') {
        alert(t('worker.dashboard.confirms.cannotRemoveHiredApplication'));
        return;
      }
      const confirmed = window.confirm(t('worker.dashboard.confirms.removeInterestConfirm'));
      if (!confirmed) return;
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('worker_id', userId)
        .eq('job_id', job.id);
      if (error) {
        alert(t('worker.dashboard.confirms.removeInterestFailed'));
      } else {
        setAppliedJobs(appliedJobs.filter(id => id !== job.id));
      }
      return;
    }

    const { error } = await supabase.from('applications').insert({
      job_id: job.id,
      worker_id: userId,
      employer_id: job.employer_id,
    });
    if (!error) {
      setAppliedJobs([...appliedJobs, job.id]);
      console.log('[notify-interest] employer_id:', job.employer_id, '| worker:', `${worker?.first_name} ${worker?.last_name}`, '| job:', job.job_title);
      supabase.from('notifications').insert({
        user_id:   job.employer_id,
        user_type: 'employer',
        type:      'new_interest',
        message:   `${(`${worker?.first_name || ''} ${worker?.last_name || ''}`).trim() || 'A worker'} expressed interest in ${job.job_title}`,
        link_to:   JSON.stringify({ tab: 'Candidates', worker_id: userId }),
      }).then((res) => { if (res.error) console.error('[notify-interest] insert error:', res.error); });
      sendNotificationEmail(supabase, {
        recipientId:   job.employer_id,
        recipientType: 'employer',
        type:          'new_interest',
        worker_name:   `${worker?.first_name || ''} ${worker?.last_name || ''}`.trim(),
        worker_trade:  trades?.trade || 'Trades worker',
        job_title:     job.job_title,
      });
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCropFile(file);
  };

  const handlePhotoCropConfirm = async (blob, outputType) => {
    setCropFile(null);
    setPhotoUploading(true);
    setPhotoMsg('');
    const path = `${worker.id}/avatar`;
    const { error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setPhotoMsgIsError(true);
      setPhotoMsg(t('worker.dashboard.myProfile.photoUploadFailed'));
      setPhotoUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('worker_profiles').update({ profile_photo_url: publicUrl }).eq('id', worker.id);
    setProfile(prev => ({ ...prev, profile_photo_url: publicUrl }));
    setPhotoMsgIsError(false);
    setPhotoMsg(t('worker.dashboard.myProfile.photoSaved'));
    setPhotoUploading(false);
  };

  const handleSaveProfile = async () => {
    if (!profileForm) return;

    const { error: workerError } = await supabase
      .from('workers')
      .update({
        first_name: profileForm.firstName,
        last_name:  profileForm.lastName,
        phone:      profileForm.phone,
        zip_code:   profileForm.zip,
      })
      .eq('id', worker.id);

    // upsert (not update) -- an org-invited worker (see OrganizationJoin.jsx's member-invite
    // redemption) has a `workers` row but no worker_profiles row yet, and .update() against a
    // WHERE clause matching zero rows succeeds with no error, silently discarding the save.
    const { error: profileError } = await supabase
      .from('worker_profiles')
      .upsert({
        id: worker.id,
        city: profileForm.city,
        state: profileForm.state,
        commute_radius_miles: profileForm.commuteRadius ? parseInt(profileForm.commuteRadius, 10) : null,
      }, { onConflict: 'id' });

    // worker_trades has no unique constraint on worker_id (only a PK on its own `id`), so
    // upsert(..., { onConflict: 'worker_id' }) isn't possible without a schema change. Update
    // first (touches only these 3 columns, leaving years_experience/min_hourly_rate/etc. from
    // WorkerStep3 intact); only insert a fresh row -- with just these 3 columns, correctly --
    // when update matched nothing, i.e. this worker never had a worker_trades row to begin with.
    const { data: updatedTradeRows, error: tradesUpdateError } = await supabase
      .from('worker_trades')
      .update({
        trade:         profileForm.trade,
        availability:  profileForm.availability,
        union_interest: profileForm.unionStatus,
      })
      .eq('worker_id', worker.id)
      .select('id');
    let tradesError = tradesUpdateError;
    if (!tradesError && (updatedTradeRows || []).length === 0) {
      const { error: tradesInsertError } = await supabase
        .from('worker_trades')
        .insert({
          worker_id:     worker.id,
          trade:         profileForm.trade,
          availability:  profileForm.availability,
          union_interest: profileForm.unionStatus,
        });
      tradesError = tradesInsertError;
    }

    if (workerError || profileError || tradesError) {
      setProfileSaveMsgIsError(true);
      setProfileSaveMsg(t('worker.dashboard.myProfile.saveError'));
    } else {
      setWorker(prev => ({ ...prev, first_name: profileForm.firstName, last_name: profileForm.lastName, phone: profileForm.phone, zip_code: profileForm.zip }));
      setProfile(prev => ({ ...prev, city: profileForm.city, state: profileForm.state, commute_radius_miles: profileForm.commuteRadius ? parseInt(profileForm.commuteRadius, 10) : null }));
      setTrades(prev => ({ ...prev, trade: profileForm.trade, availability: profileForm.availability, union_interest: profileForm.unionStatus }));
      setProfileSaveMsgIsError(false);
      setProfileSaveMsg(t('worker.dashboard.myProfile.saveSuccess'));
      setTimeout(() => setProfileSaveMsg(''), 3000);
    }
  };

  const handleGenerateResume = async () => {
    const [expRes, certsRes, licsRes] = await Promise.all([
      supabase.from('worker_experience').select('*').eq('worker_id', worker.id).order('start_year', { ascending: false }),
      supabase.from('worker_certifications').select('*').eq('worker_id', worker.id).order('created_at', { ascending: false }),
      supabase.from('worker_licenses').select('*').eq('worker_id', worker.id).order('created_at', { ascending: false }),
    ]);
    const exp   = expRes.data   || [];
    const certs = certsRes.data || [];
    const lics  = licsRes.data  || [];

    const esc = s => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const loc = [profile?.city, profile?.state].filter(Boolean).join(', ');
    const avail = trades?.availability || '';
    const union = trades?.union_interest === 'both'     ? 'Open to union and non-union'
                : trades?.union_interest === 'union'    ? 'Union only'
                : trades?.union_interest === 'employer' ? 'Non-union' : '';

    const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${esc(fullName)} — Resume</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; color: #111; background: #fff; }
  @page { margin: 1in; }
  @media screen { body { padding: 48px; max-width: 760px; margin: 0 auto; } }
  h1 { font-size: 22pt; font-weight: 700; margin-bottom: 5px; }
  .contact { font-size: 9.5pt; color: #555; margin-bottom: 22px; }
  .contact span + span::before { content: ' · '; }
  .section { margin-bottom: 18px; }
  .section-title { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.09em; border-bottom: 1.5px solid #111; padding-bottom: 3px; margin-bottom: 10px; }
  .bio { font-size: 10pt; line-height: 1.55; }
  .skills { display: flex; flex-wrap: wrap; gap: 4px 28px; font-size: 10pt; }
  .exp-entry { margin-bottom: 13px; }
  .exp-top { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  .exp-role { font-weight: 700; }
  .exp-dates { font-size: 9.5pt; color: #555; white-space: nowrap; flex-shrink: 0; }
  .exp-company { font-size: 9.5pt; color: #444; margin: 2px 0 4px; }
  .exp-desc { font-size: 10pt; color: #333; line-height: 1.5; white-space: pre-wrap; }
  .row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 8px; }
  .row-left { flex: 1; }
  .row-right { font-size: 9.5pt; color: #555; white-space: nowrap; flex-shrink: 0; }
  .item-name { font-weight: 600; }
  .item-sub { font-size: 9.5pt; color: #555; margin-top: 1px; }
  .verified { color: #1a7a4a; font-size: 9pt; font-weight: 700; margin-left: 6px; }
</style>
</head>
<body>
  <h1>${esc(fullName)}</h1>
  <div class="contact">
    ${[loc, worker?.phone, worker?.email].filter(Boolean).map(v => `<span>${esc(v)}</span>`).join('')}
  </div>

  ${profile?.bio ? `
  <div class="section">
    <div class="section-title">Summary</div>
    <div class="bio">${esc(profile.bio)}</div>
  </div>` : ''}

  ${(trades?.trade || avail || union) ? `
  <div class="section">
    <div class="section-title">Trade &amp; Skills</div>
    <div class="skills">
      ${trades?.trade ? `<div><strong>Trade:</strong> ${esc(trades.trade)}</div>` : ''}
      ${avail ? `<div><strong>Availability:</strong> ${avail}</div>` : ''}
      ${union ? `<div><strong>Union status:</strong> ${union}</div>` : ''}
    </div>
  </div>` : ''}

  ${exp.length > 0 ? `
  <div class="section">
    <div class="section-title">Work Experience</div>
    ${exp.map(e => `
    <div class="exp-entry">
      <div class="exp-top">
        <div class="exp-role">${esc(e.role)}</div>
        <div class="exp-dates">${e.start_year || ''}${(e.end_year || e.current) ? ` – ${e.current ? 'Present' : e.end_year}` : ''}</div>
      </div>
      <div class="exp-company">${esc(e.company)}</div>
      ${e.description ? `<div class="exp-desc">${esc(e.description)}</div>` : ''}
    </div>`).join('')}
  </div>` : ''}

  ${certs.length > 0 ? `
  <div class="section">
    <div class="section-title">Certifications</div>
    ${certs.map(c => `
    <div class="row">
      <div class="row-left">
        <div class="item-name">${esc(c.certification_name)}${c.verified ? '<span class="verified">✓ Verified</span>' : ''}</div>
        ${c.issuing_organization ? `<div class="item-sub">${esc(c.issuing_organization)}</div>` : ''}
      </div>
      ${c.expiry_date ? `<div class="row-right">Exp: ${fmtDate(c.expiry_date)}</div>` : ''}
    </div>`).join('')}
  </div>` : ''}

  ${lics.length > 0 ? `
  <div class="section">
    <div class="section-title">Licenses</div>
    ${lics.map(l => `
    <div class="row">
      <div class="row-left">
        <div class="item-name">${esc(l.license_name)}</div>
        <div class="item-sub">${[l.license_number ? `#${esc(l.license_number)}` : '', esc(l.state)].filter(Boolean).join(' · ')}</div>
      </div>
      ${l.expiry_date ? `<div class="row-right">Exp: ${fmtDate(l.expiry_date)}</div>` : ''}
    </div>`).join('')}
  </div>` : ''}

</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right    = '0';
    iframe.style.bottom   = '0';
    iframe.style.width    = '0';
    iframe.style.height   = '0';
    iframe.style.border   = '0';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  const handleAddExperience = async () => {
    const { data, error } = await supabase
      .from('worker_experience')
      .insert({
        worker_id:   worker.id,
        company:     expForm.company,
        role:        expForm.role,
        start_year:  parseInt(expForm.start_year) || null,
        end_year:    expForm.current ? null : (parseInt(expForm.end_year) || null),
        current:     expForm.current,
        description: expForm.description,
      })
      .select()
      .single();
    if (!error && data) {
      setExperience(prev => [data, ...prev]);
      setShowExpForm(false);
      setExpForm({ company: '', role: '', start_year: '', end_year: '', current: false, description: '' });
    }
  };

  const handleDeleteExperience = async (id) => {
    if (!window.confirm(t('worker.dashboard.confirms.deleteExperienceConfirm'))) return;
    const { error } = await supabase.from('worker_experience').delete().eq('id', id);
    if (!error) setExperience(experience.filter(e => e.id !== id));
  };

  const handleAddCert = async () => {
    if (!certForm.certification_name.trim()) {
      setCertFormMsg(t('worker.dashboard.experience.certNameRequiredError'));
      setCertFormMsgIsError(true);
      return;
    }
    setCertFormMsg('');

    let documentUrl = null;
    if (certDocFile) {
      setCertDocUploading(true);
      const safeName = certForm.certification_name.replace(/[^a-zA-Z0-9]/g, '_');
      const path = `${worker.id}/${safeName}/document`;
      console.log('[cert-upload] starting upload:', { bucket: 'certification-documents', path, fileType: certDocFile.type, fileSize: certDocFile.size });
      const { data: uploadData, error: uploadErr } = await supabase.storage
        .from('certification-documents')
        .upload(path, certDocFile, { upsert: true, contentType: certDocFile.type });
      if (uploadErr) {
        console.error('[cert-upload] upload failed:', uploadErr);
      } else {
        console.log('[cert-upload] upload success:', uploadData);
        const { data: urlData } = supabase.storage.from('certification-documents').getPublicUrl(path);
        documentUrl = urlData.publicUrl;
        console.log('[cert-upload] public URL:', documentUrl);
      }
      setCertDocUploading(false);
    }

    const { data, error } = await supabase
      .from('worker_certifications')
      .insert({
        worker_id:            worker.id,
        certification_name:   certForm.certification_name,
        issuing_organization: certForm.issuing_organization || null,
        issue_date:           certForm.issue_date  || null,
        expiry_date:          certForm.expiry_date || null,
        certificate_number:   certForm.certificate_number || null,
        document_url:         documentUrl,
        verification_status:  documentUrl ? 'pending' : 'unverified',
      })
      .select().single();
    if (!error && data) {
      setCertifications(prev => [data, ...prev]);
      setShowCertForm(false);
      setCertForm({ certification_name: '', issuing_organization: '', issue_date: '', expiry_date: '', certificate_number: '' });
      setCertDocFile(null);
      setHasCerts(true);
      setCertFormMsg('');
    } else {
      console.error('[cert-save] insert failed:', error);
      setCertFormMsg(t('worker.dashboard.experience.certSaveFailedError'));
      setCertFormMsgIsError(true);
    }
  };

  const handleDeleteCert = async (id) => {
    if (!window.confirm(t('worker.dashboard.confirms.deleteCertConfirm'))) return;
    const { error } = await supabase.from('worker_certifications').delete().eq('id', id);
    if (!error) {
      const updated = certifications.filter(c => c.id !== id);
      setCertifications(updated);
      setHasCerts(updated.length > 0);
    }
  };

  const handleAddLicense = async () => {
    if (!licenseForm.license_name.trim()) {
      setLicFormMsg(t('worker.dashboard.experience.licenseNameRequiredError'));
      setLicFormMsgIsError(true);
      return;
    }
    setLicFormMsg('');

    let documentUrl = null;
    if (licDocFile) {
      setLicDocUploading(true);
      const safeName = licenseForm.license_name.replace(/[^a-zA-Z0-9]/g, '_');
      const path = `${worker.id}/licenses/${safeName}/document`;
      const { error: uploadErr } = await supabase.storage
        .from('certification-documents')
        .upload(path, licDocFile, { upsert: true, contentType: licDocFile.type });
      if (!uploadErr) {
        const { data: urlData } = supabase.storage.from('certification-documents').getPublicUrl(path);
        documentUrl = urlData.publicUrl;
      }
      setLicDocUploading(false);
    }

    const { data, error } = await supabase
      .from('worker_licenses')
      .insert({
        worker_id:      worker.id,
        license_name:   licenseForm.license_name,
        license_number: licenseForm.license_number || null,
        state:          licenseForm.state          || null,
        issue_date:     licenseForm.issue_date     || null,
        expiry_date:    licenseForm.expiry_date    || null,
        document_url:   documentUrl,
      })
      .select().single();
    if (!error && data) {
      setLicenses(prev => [data, ...prev]);
      setShowLicenseForm(false);
      setLicenseForm({ license_name: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
      setLicDocFile(null);
      setHasLicenses(true);
      setLicFormMsg('');
    } else {
      console.error('[license-save] insert failed:', error);
      setLicFormMsg(t('worker.dashboard.experience.licenseSaveFailedError'));
      setLicFormMsgIsError(true);
    }
  };

  const handleResumeUpload = async (file) => {
    if (!file || !worker?.id) return;
    setResumeUploading(true);
    const path = `${worker.id}/resume/document`;
    const { error: uploadErr } = await supabase.storage
      .from('certification-documents')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (!uploadErr) {
      const { data: urlData } = supabase.storage.from('certification-documents').getPublicUrl(path);
      const url = urlData.publicUrl;
      await supabase.from('workers').update({ resume_url: url }).eq('id', worker.id);
      setResumeUrl(url);
    }
    setResumeUploading(false);
  };

  const handleDeleteLicense = async (id) => {
    if (!window.confirm(t('worker.dashboard.confirms.deleteLicenseConfirm'))) return;
    const { error } = await supabase.from('worker_licenses').delete().eq('id', id);
    if (!error) {
      const updated = licenses.filter(l => l.id !== id);
      setLicenses(updated);
      setHasLicenses(updated.length > 0);
    }
  };

  const handleAddPortfolio = async () => {
    if (!portfolioForm.title.trim()) return;
    let videoUrl = null;
    let videoType = null;

    if (portfolioLinkMode && portfolioForm.videoUrl.trim()) {
      videoUrl = portfolioForm.videoUrl.trim();
      videoType = detectVideoType(videoUrl);
    } else if (!portfolioLinkMode && portfolioVideoFile) {
      setPortfolioVideoUploading(true);
      setPortfolioVideoProgress(0);
      const safeTitle = portfolioForm.title.replace(/[^a-zA-Z0-9]/g, '_');
      const path = `${worker.id}/${safeTitle}/video`;
      const { error: uploadErr } = await supabase.storage
        .from('worker-videos')
        .upload(path, portfolioVideoFile, { upsert: true, contentType: portfolioVideoFile.type });
      if (uploadErr) {
        console.error('[portfolio-upload] upload failed:', uploadErr);
        setPortfolioVideoUploading(false);
        return;
      }
      const { data: urlData } = supabase.storage.from('worker-videos').getPublicUrl(path);
      videoUrl = urlData.publicUrl;
      videoType = 'upload';
      setPortfolioVideoUploading(false);
      setPortfolioVideoProgress(100);
    }

    const { data, error } = await supabase
      .from('worker_portfolio')
      .insert({
        worker_id:   worker.id,
        title:       portfolioForm.title,
        description: portfolioForm.description || null,
        video_url:   videoUrl,
        video_type:  videoType,
      })
      .select().single();
    if (!error && data) {
      setPortfolio(prev => [data, ...prev]);
      setShowPortfolioForm(false);
      setPortfolioForm({ title: '', description: '', videoUrl: '' });
      setPortfolioVideoFile(null);
      setPortfolioLinkMode(false);
      setPortfolioVideoProgress(0);
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm(t('worker.dashboard.confirms.deletePortfolioConfirm'))) return;
    const { error } = await supabase.from('worker_portfolio').delete().eq('id', id);
    if (!error) setPortfolio(prev => prev.filter(p => p.id !== id));
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirmPw) { setPwMsgIsSuccess(false); setPwMsg(t('worker.dashboard.settings.passwordMismatch')); return; }
    if (pwForm.newPw.length < 8) { setPwMsgIsSuccess(false); setPwMsg(t('worker.dashboard.settings.passwordTooShort')); return; }
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
    if (error) {
      setPwMsgIsSuccess(false);
      setPwMsg(error.message);
    } else {
      setPwMsgIsSuccess(true);
      setPwMsg(t('worker.dashboard.settings.passwordUpdated'));
      setPwForm({ newPw: '', confirmPw: '' });
      setTimeout(() => setPwMsg(''), 3000);
    }
  };

  const handleToggleEmailNotif = async () => {
    const newVal = !emailNotif;
    setEmailNotif(newVal);
    await supabase.from('workers').update({ email_notifications: newVal }).eq('id', worker.id);
  };

  const handleDismissNotif = async (notifId) => {
    await supabase.from('dismissed_notifications').insert({ worker_id: worker.id, profile_view_id: notifId });
    setProfileViewNotifs(prev => prev.filter(n => n.id !== notifId));
  };

  const handleClearAllNotifs = async () => {
    const rows = profileViewNotifs.map(n => ({ worker_id: worker.id, profile_view_id: n.id }));
    await supabase.from('dismissed_notifications').insert(rows);
    setProfileViewNotifs([]);
  };

  const handleToggleSave = async (job, e) => {
    e.stopPropagation();
    if (savedJobIds.has(job.id)) {
      await supabase.from('saved_jobs').delete().eq('worker_id', worker.id).eq('job_id', job.id);
      setSavedJobIds(prev => { const next = new Set(prev); next.delete(job.id); return next; });
    } else {
      await supabase.from('saved_jobs').insert({ worker_id: worker.id, job_id: job.id });
      setSavedJobIds(prev => new Set([...prev, job.id]));
    }
  };

  const handleRemoveSaved = async (jobId, e) => {
    e.stopPropagation();
    await supabase.from('saved_jobs').delete().eq('worker_id', worker.id).eq('job_id', jobId);
    setSavedJobIds(prev => { const next = new Set(prev); next.delete(jobId); return next; });
    setSavedJobs(prev => prev.filter(j => j.id !== jobId));
  };

  const openDismissModal = (job, e) => {
    e.stopPropagation();
    setDismissReason('');
    setDismissDetail('');
    setDismissJobModal(job);
  };

  const removeDismissedJobFromView = (jobId) => {
    setJobs(prev => prev.filter(j => j.id !== jobId));
    setAllJobs(prev => prev.filter(j => j.id !== jobId));
  };

  const handleConfirmDismiss = async () => {
    if (!dismissJobModal) return;
    setDismissSubmitting(true);
    const { error } = await supabase.from('job_dismissals').insert({
      job_id: dismissJobModal.id,
      worker_id: worker.id,
      reason: dismissReason || null,
      reason_detail: dismissReason === 'Other' ? (dismissDetail.trim() || null) : null,
    });
    // A duplicate-dismissal conflict (unique constraint) is harmless — the job
    // is already dismissed, so just remove it from view either way.
    if (error) console.warn('job_dismissals insert:', error.message);
    removeDismissedJobFromView(dismissJobModal.id);
    setDismissSubmitting(false);
    setDismissJobModal(null);
  };

  const setExp  = (k, v) => setExpForm(p => ({ ...p, [k]: v }));
  const setPf   = (k, v) => setProfileForm(p => ({ ...p, [k]: v }));
  const setCert = (k, v) => setCertForm(p => ({ ...p, [k]: v }));
  const setLic  = (k, v) => setLicenseForm(p => ({ ...p, [k]: v }));

  const fmtDate = (d) => {
    if (!d) return null;
    const [year, month] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const handleLoadOffer = async (appId, appStatus) => {
    if (appOffers[appId] != null && appStatus !== 'offer_sent') return;
    setOfferLoading(appId);
    const { data } = await supabase.from('job_offers').select('*').eq('application_id', appId).order('created_at', { ascending: false }).limit(1).maybeSingle();
    setAppOffers(prev => ({ ...prev, [appId]: data || null }));
    // Who specifically on the employer's team sent this -- created_by is the real acting
    // individual's own auth.uid(), not the shared employer_id, so it needs its own lookup
    // against employer_staff (readable here via the relationship this application itself proves).
    if (data?.created_by && data?.employer_id) {
      const { data: senderRow } = await supabase
        .from('employer_staff')
        .select('display_name, avatar_url')
        .eq('employer_id', data.employer_id)
        .eq('user_id', data.created_by)
        .maybeSingle();
      setOfferSenders(prev => ({ ...prev, [appId]: senderRow || null }));
    }
    setOfferLoading(null);
  };

  const handleAcceptOffer = async (app, offer) => {
    const acceptedAt  = new Date().toISOString();
    const signature   = acceptSignature.trim();
    await supabase.from('job_offers').update({ status: 'accepted', accepted_signature: signature, accepted_at: acceptedAt }).eq('id', offer.id);
    await supabase.from('applications').update({ status: 'hired' }).eq('id', app.id);
    if (app.job_id) {
      const { error: markFilledError } = await supabase.rpc('mark_job_filled', { p_job_id: app.job_id });
      if (markFilledError) console.warn('mark_job_filled RPC failed (non-blocking):', markFilledError.message);
    }
    setApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: 'hired' } : a));
    setAcceptConfirmModal(null);
    setAcceptConfirmChecked(false);
    setAcceptSignature('');
    const companyName = app.employer?.company_name || '';
    const jobTitle    = offer.job_title || app.job?.job_title || '';
    const signedAtStr = new Date(acceptedAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    setHireCelebration({
      companyName,
      jobTitle,
      hireDate:   acceptedAt,
      employerId: offer.employer_id || app.employer_id,
      jobId:      offer.job_id || app.job_id,
    });
    supabase.from('notifications').insert({
      user_id:   app.employer_id,
      user_type: 'employer',
      type:      'offer_accepted',
      message:   `${fullName} accepted your offer for ${jobTitle}`,
      link_to:   JSON.stringify({ tab: 'Candidates', worker_id: worker.id }),
    }).then(() => {});
    const sharedOfferFields = {
      job_title:     jobTitle,
      company_name:  companyName,
      offered_pay:       offer.offered_pay        || '',
      pay_schedule:      offer.pay_schedule        || '',
      start_date:        offer.start_date          || '',
      work_location:     offer.work_location       || '',
      work_hours:        offer.work_hours           || '',
      overtime_availability: offer.overtime_availability || '',
      benefits:          (offer.benefits || []).join(', '),
      first_day_instructions: offer.first_day_instructions || '',
      accepted_signature: signature,
      signed_at:          signedAtStr,
    };
    sendNotificationEmail(supabase, {
      recipientId:   app.employer_id,
      recipientType: 'employer',
      type:          'offer_accepted',
      worker_name:   fullName,
      ...sharedOfferFields,
    });
    sendNotificationEmail(supabase, {
      recipientId:   worker.id,
      recipientType: 'worker',
      type:          'offer_accepted_worker',
      worker_name:   fullName,
      ...sharedOfferFields,
    });
  };

  const handleDeclineOffer = async (app, offer) => {
    await supabase.from('job_offers').update({ status: 'declined' }).eq('id', offer.id);
    await supabase.from('applications').update({ status: 'reviewing' }).eq('id', app.id);
    setApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: 'reviewing' } : a));
    supabase.from('notifications').insert({
      user_id:   app.employer_id,
      user_type: 'employer',
      type:      'offer_declined',
      message:   `${fullName} declined your offer for ${app.job?.job_title}`,
      link_to:   JSON.stringify({ tab: 'Candidates', worker_id: worker.id }),
    }).then(() => {});
    sendNotificationEmail(supabase, {
      recipientId:   app.employer_id,
      recipientType: 'employer',
      type:          'offer_declined',
      worker_name:   fullName,
      job_title:     app.job?.job_title || '',
    });
  };

  const handleArchiveApplication = async (applicationId) => {
    const archivedAt = new Date().toISOString();
    await supabase.from('applications').update({ worker_archived_at: archivedAt }).eq('id', applicationId);
    setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, worker_archived_at: archivedAt } : a));
  };

  const handleUnarchiveApplication = async (applicationId) => {
    await supabase.from('applications').update({ worker_archived_at: null }).eq('id', applicationId);
    setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, worker_archived_at: null } : a));
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm(t('worker.dashboard.confirms.removeApplicationConfirm'))) return;
    await supabase.from('applications').update({ worker_hidden_at: new Date().toISOString() }).eq('id', applicationId);
    setApplications(prev => prev.filter(a => a.id !== applicationId));
  };

  const filteredJobs = jobSearch.trim()
    ? jobs.filter(job => {
        const q = jobSearch.toLowerCase();
        return (
          (job.job_title        || '').toLowerCase().includes(q) ||
          (job.company_name     || '').toLowerCase().includes(q) ||
          (job.job_description  || '').toLowerCase().includes(q) ||
          (job.city             || '').toLowerCase().includes(q) ||
          (job.state            || '').toLowerCase().includes(q)
        );
      })
    : jobs;

  const renderCompanyInfo = (job) => {
    const emp = employerDetailsCache[job?.employer_id];
    if (!emp) return null;
    const hasAny = emp.about || emp.website || emp.year_founded || emp.industry || emp.union_status || emp.typical_benefits?.length > 0;
    if (!hasAny) return null;
    return (
      <div style={{ marginBottom: '16px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '14px', marginTop: '4px' }}>
        <div style={{ fontSize: '11px', color: AMBER, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
          {t('worker.dashboard.shared.aboutCompany', { company: job.company_name })}
        </div>
        {emp.about && (
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: '10px', whiteSpace: 'pre-wrap' }}>
            {emp.about}
          </div>
        )}
        {(emp.year_founded || emp.industry || emp.union_status || emp.website) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: emp.typical_benefits?.length > 0 ? '10px' : '0' }}>
            {emp.year_founded && (
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>{t('worker.dashboard.shared.foundedPrefix')}</span>{emp.year_founded}
              </span>
            )}
            {emp.industry && (
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{emp.industry}</span>
            )}
            {emp.union_status && (
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{emp.union_status}</span>
            )}
            {emp.website && (
              <a href={emp.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ fontSize: '12px', color: AMBER, textDecoration: 'none' }}>
                {t('worker.dashboard.shared.websiteLink')}
              </a>
            )}
          </div>
        )}
        {emp.typical_benefits?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {emp.typical_benefits.map((b, i) => (
              <span key={i} style={{ fontSize: '11px', fontWeight: 600, color: NAVY, backgroundColor: AMBER, borderRadius: '20px', padding: '3px 9px' }}>
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh' }}>

      {/* ── Top Navbar ─────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: NAV_H,
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', zIndex: 100,
      }}>
        <div onClick={() => { setActiveTab('Dashboard'); setRefreshKey(k => k + 1); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={isMobile ? 28 : 36} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div className="dash-nav-badge" style={{
            backgroundColor: 'rgba(29,158,117,0.14)', border: `1px solid ${GREEN}`,
            borderRadius: '999px', padding: '5px 14px',
            fontSize: '0.77rem', fontWeight: 600, color: GREEN,
          }}>
            {t('common.freeForWorkers')}
          </div>
          <LanguageToggle userId={worker?.id} />
          <NotificationBell userId={worker?.id} onNavigate={(tab, link) => {
            setActiveTab(tab);
            if (tab === 'Applications') setApplicationsKey(k => k + 1);
            if (link?.conversation_id) setOpenConversationId(link.conversation_id);
            if (link?.employer_id) navigate('/company/' + link.employer_id);
            if (link?.application_id) setHighlightAppId(link.application_id);
            if (link?.apprenticeship_id) { setApprenticeshipSearch(''); setHighlightApprId(link.apprenticeship_id); }
          }} />
          {isMobile && (
            <div onClick={() => setActiveTab('Settings')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: WHITE }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
          )}
          <Avatar initials={initials} size={36} photoUrl={profile?.profile_photo_url} />
        </div>
      </nav>

      {/* ── Left Sidebar ────────────────────────────────────── */}
      <aside className="dash-sidebar" style={{
        position: 'fixed', top: NAV_H, left: 0, bottom: 0, width: SIDEBAR_W,
        backgroundColor: NAVY_MID, zIndex: 99,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Avatar initials={initials} size={52} photoUrl={profile?.profile_photo_url} />
          </div>
          <p style={{ marginTop: '10px', fontWeight: 700, fontSize: '0.93rem', color: WHITE }}>{fullName}</p>
          <p style={{ fontSize: '0.77rem', color: AMBER, marginTop: '3px', lineHeight: 1.4 }}>
            {tradeLabel || t('worker.dashboard.shared.completeYourProfile')}
          </p>
          {(profile?.city || profile?.state) && (
            <p style={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.42)', marginTop: '3px' }}>
              {[profile.city, profile.state].filter(Boolean).join(', ')}
            </p>
          )}
          <div style={{ marginTop: '14px' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: profilePct === 100 ? AMBER : 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
              {profilePct === 100 ? t('worker.dashboard.shared.profileCompleteFull') : t('worker.dashboard.shared.profilePercentComplete', { pct: profilePct })}
            </p>
            <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${profilePct}%`, height: '100%', backgroundColor: AMBER, borderRadius: '2px' }} />
            </div>
            {missingItems.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {missingItems.map(item => (
                  <button
                    key={item.label}
                    onClick={() => setActiveTab(item.tab)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: FONT_BODY }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: AMBER, lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.72rem', color: AMBER, fontWeight: 500 }}>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming interview(s) */}
        {upcomingInterviews.length > 0 && (
          <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {upcomingInterviews.map((interview) => {
              const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
              const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
              return (
                <div
                  key={interview.id}
                  onClick={() => openUpcomingInterview(interview)}
                  style={{ backgroundColor: NAVY, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 8px 8px 0', padding: '10px 12px', cursor: 'pointer' }}
                >
                  <p style={{ fontSize: '0.64rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                    {t('messaging.upcomingInterview')}
                  </p>
                  <p style={{ fontSize: '0.78rem', color: WHITE, fontWeight: 600, marginBottom: '2px', overflowWrap: 'break-word' }}>
                    {interview.otherPartyName}
                  </p>
                  <p style={{ fontSize: '0.72rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                    {countdown.text}
                  </p>
                  {interview.status === 'accepted' && (
                    <p style={{ fontSize: '0.68rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}>{t('messaging.statusConfirmed')}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Sign out — pinned to bottom of sidebar */}
        <div style={{ marginTop: 'auto', padding: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: '8px', width: '100%',
              color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {t('common.signOut')}
          </button>
        </div>

        {/* Legal links — pinned below Sign out */}
        <div style={{ padding: '0 14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <LegalLinksRow fontSize="0.68rem" wrap />
          <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────── */}
      <main className="dash-main" style={{
        marginLeft: SIDEBAR_W, marginTop: NAV_H,
        height: `calc(100dvh - ${NAV_H}px)`,
        backgroundColor: NAVY,
        display: 'flex', flexDirection: 'column',
        overflowY: isMobile ? 'auto' : 'hidden', overflowX: 'hidden',
      }}>

        {/* Tab bar — desktop only */}
        {!isMobile && (
          <div className="dash-tab-bar" style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {NAV_GROUPS.map(group => {
              if (group.tabs) {
                return (
                  <NavDropdown
                    key={group.label}
                    label={tabLabel(group.label)}
                    active={group.tabs.includes(activeTab)}
                    items={group.tabs.map(tab => ({
                      label: tabLabel(tab),
                      active: activeTab === tab,
                      badge: (tab === 'Applications' && appsBadge) || (tab === 'Jobs' && jobsBadge),
                      onClick: () => { if (tab === 'Union Wages') { navigate('/union-wages'); return; } setActiveTab(tab); if (tab === 'Applications') setApplicationsKey(k => k + 1); },
                    }))}
                  />
                );
              }
              const tab = group.tab;
              const msgBadge = tab === 'Messages' && unreadCount > 0;
              return (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); if (tab === 'Applications') setApplicationsKey(k => k + 1); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '12px 18px', fontSize: '0.86rem', fontWeight: 600,
                    fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                    color: activeTab === tab ? AMBER : 'rgba(255,255,255,0.4)',
                    borderBottom: activeTab === tab ? `2px solid ${AMBER}` : '2px solid transparent',
                    marginBottom: '-1px', position: 'relative',
                  }}
                >
                  {tabLabel(tab)}
                  {msgBadge && (
                    <span style={{ position: 'absolute', top: '6px', right: '2px', minWidth: '16px', height: '16px', borderRadius: '8px', backgroundColor: AMBER, color: NAVY, fontSize: '8px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile grouped sub-tab bar -- reuses the same expand-below-header pattern for
            whichever bottom-nav group icon (Jobs / Resources / Profile) was tapped. */}
        {isMobile && (mobileJobsOpen || mobileResourcesOpen || mobileProfileOpen) && (
          <div style={{ flexShrink: 0, overflowX: 'hidden', overflowY: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', touchAction: 'pan-x', scrollSnapType: 'x mandatory' }}>
              {(mobileJobsOpen ? ['Jobs', 'Apprenticeships', 'Applications', 'Saved Jobs']
                : mobileResourcesOpen ? RESOURCE_TABS
                : ['My Profile', 'Applications', 'Experience', 'Saved Jobs', 'Check-ins', 'Settings']
              ).map(tab => (
                <button
                  key={tab}
                  onClick={() => { if (tab === 'Union Wages') { navigate('/union-wages'); return; } setActiveTab(tab); if (tab === 'Applications') { setAppsBadge(false); setApplicationsKey(k => k + 1); } }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '12px 18px', fontSize: '0.86rem', fontWeight: 600,
                    fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                    color: activeTab === tab ? AMBER : 'rgba(255,255,255,0.4)',
                    borderBottom: activeTab === tab ? `2px solid ${AMBER}` : '2px solid transparent',
                    marginBottom: '-1px', scrollSnapAlign: 'start', position: 'relative',
                  }}
                >
                  {tabLabel(tab)}
                  {tab === 'Applications' && appsBadge && (
                    <span style={{ position: 'absolute', top: '8px', right: '4px', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: AMBER }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Signup steps a worker can arrive without (org-invite members, older accounts): no
            worker_profiles row, or no worker_trades row / no trade. Persistent on every tab and
            non-blocking -- the dashboard stays usable -- with a button to the first missing step.
            It clears itself once the rows exist (My Profile's save creates them too). */}
        {(() => {
          const missingProfile = setupRowsChecked.profile && !profile;
          const missingTrade   = setupRowsChecked.trades && !trades?.trade;
          if (!missingProfile && !missingTrade) return null;
          if (activeTab === 'Messages' && chatThreadOpen) return null;
          return (
            <div data-testid="finish-setup-banner" role="status" style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px 14px', flexWrap: 'wrap',
              background: 'rgba(245,166,35,0.1)', borderBottom: '1px solid rgba(245,166,35,0.35)', padding: '10px 16px',
            }}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <p style={{ fontSize: '0.86rem', fontWeight: 700, color: AMBER }}>{t('worker.dashboard.setupBanner.title')}</p>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.45, marginTop: '2px' }}>
                  {t(missingProfile ? 'worker.dashboard.setupBanner.bodyProfile' : 'worker.dashboard.setupBanner.bodyTrade')}
                </p>
              </div>
              <button
                onClick={() => navigate(missingProfile ? '/worker/signup/personal' : '/worker/signup/trade')}
                style={{ flexShrink: 0, minHeight: isMobile ? '44px' : undefined, background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.8rem', padding: '8px 14px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {t('worker.dashboard.setupBanner.button')}
              </button>
            </div>
          );
        })()}

        {/* ── Messages ── */}
        {activeTab === 'Messages' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: chatThreadOpen ? 'none' : 'block', flexShrink: 0 }}>
              <SupportInbox supabase={supabase} accountType="worker" accountId={worker.id} viewerId={worker.id} />
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Messaging key={openConversationId || 'default'} supabase={supabase} userId={worker.id} userType="worker" senderName={fullName} initialConversationId={openConversationId} onViewOffer={appId => { setActiveTab('Applications'); setApplicationsKey(k => k + 1); setHighlightAppId(appId); }} onMobileThreadChange={setChatThreadOpen} onUnreadCountChange={(value, isAbsolute) => {
                if (isAbsolute) {
                  setUnreadCount(value);
                  setMobileMessageBadge(value);
                } else {
                  setUnreadCount(prev => prev + value);
                  setMobileMessageBadge(prev => prev + value);
                }
              }} />
            </div>
          </div>
        )}

        {/* ── Check-ins: coaching/support video calls with connected organizations, ── */}
        {/* separate from job interviews (see CheckInPanel / organization_checkins). */}
        {activeTab === 'Check-ins' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>
            {/* No connected organization: explain what this tab is for. Connected: who, what they
                can see, and a Disconnect with an on-page confirm step (sets left_at). */}
            {membershipsLoaded && myMemberships.length === 0 && (
              <div data-checkins-empty style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '20px', marginBottom: '16px', maxWidth: '640px' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: WHITE, marginBottom: '8px', lineHeight: 1.35 }}>{t('worker.checkins.emptyTitle')}</h2>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>{t('worker.checkins.emptyBody')}</p>
              </div>
            )}
            {myMemberships.map(m => (
              <div key={m.id} data-checkins-org style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px', marginBottom: '16px', maxWidth: '640px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  {m.logo_url ? (
                    <img src={m.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: '10px', objectFit: 'cover', flexShrink: 0, backgroundColor: WHITE }} />
                  ) : (
                    <div aria-hidden="true" style={{ width: 48, height: 48, borderRadius: '10px', backgroundColor: AMBER, color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.2rem', flexShrink: 0 }}>
                      {(m.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', marginBottom: '2px' }}>{t('worker.checkins.connectedWith')}</p>
                    <p style={{ fontSize: '1.05rem', fontWeight: 700, color: WHITE, overflowWrap: 'anywhere' }}>{m.name}</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.55, marginBottom: '12px' }}>{t('worker.checkins.canSee', { name: m.name })}</p>
                {confirmDisconnectId === m.id ? (
                  <div data-disconnect-confirm role="alertdialog" aria-label={t('worker.checkins.disconnectConfirmTitle', { name: m.name })} style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: '10px', padding: '12px 14px' }}>
                    <p style={{ fontSize: '0.9rem', fontWeight: 700, color: WHITE, marginBottom: '4px' }}>{t('worker.checkins.disconnectConfirmTitle', { name: m.name })}</p>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.5, marginBottom: '10px' }}>{t('worker.checkins.disconnectConfirmBody')}</p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => handleLeaveOrganization(m, { skipConfirm: true })} disabled={leavingOrgId === m.id}
                        style={{ minHeight: '44px', padding: '0 16px', borderRadius: '8px', border: 'none', backgroundColor: '#DC2626', color: WHITE, fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT_BODY, cursor: leavingOrgId === m.id ? 'default' : 'pointer', opacity: leavingOrgId === m.id ? 0.7 : 1 }}>
                        {leavingOrgId === m.id ? t('worker.checkins.disconnecting') : t('worker.checkins.disconnectYes')}
                      </button>
                      <button type="button" onClick={() => setConfirmDisconnectId(null)}
                        style={{ minHeight: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: WHITE, fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setLeaveError(''); setConfirmDisconnectId(m.id); }}
                    style={{ minHeight: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.5)', background: 'transparent', color: '#F87171', fontSize: '0.9rem', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }}>
                    {t('worker.checkins.disconnect')}
                  </button>
                )}
                {leaveError && confirmDisconnectId === m.id && (
                  <p role="alert" style={{ fontSize: '0.875rem', color: '#F87171', marginTop: '8px' }}>{leaveError}</p>
                )}
              </div>
            ))}
            <CheckInPanel
              viewerType="worker"
              viewerId={worker.id}
              counterparts={connectedOrgs}
              isMobile={isMobile}
              hideNoCounterpartsNotice
            />
          </div>
        )}

        {/* ── Applications ── */}
        {activeTab === 'Applications' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('worker.dashboard.applications.subtitle')}
            </p>

            {/* Active / Archived filter tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
              {['active', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setApplicationsArchiveFilter(f)}
                  style={{
                    background: applicationsArchiveFilter === f ? AMBER : 'transparent',
                    color: applicationsArchiveFilter === f ? NAVY : 'rgba(255,255,255,0.6)',
                    border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(`worker.dashboard.applications.filter${f === 'active' ? 'Active' : 'Archived'}`)}
                </button>
              ))}
            </div>

            {applications.some(a => a.hasReceivedOffer) && (
              <div style={{ marginBottom: '14px' }}>
                <CompareEntryButton
                  count={offerCompare.ids.length}
                  label={t('compare.worker.offers.entryButton')}
                  onClick={() => setOpenCompare('offers')}
                />
              </div>
            )}

            {(() => {
              const applicationsForView = applications.filter(a =>
                applicationsArchiveFilter === 'archived' ? !!a.worker_archived_at : !a.worker_archived_at
              );
              return loadingApps ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px', marginTop: '32px' }}>{t('common.loading')}</div>
            ) : applicationsForView.length === 0 ? (
              <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '32px 20px', textAlign: 'center' }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginBottom: '16px' }}>
                  {applicationsArchiveFilter === 'archived' ? t('worker.dashboard.applications.noArchived') : t('worker.dashboard.applications.noneYet')}
                </p>
                {applicationsArchiveFilter !== 'archived' && (
                  <button onClick={() => setActiveTab('Dashboard')} style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '13px', fontWeight: 700, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}>
                    {t('worker.dashboard.applications.browseJobs')}
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {applicationsForView.map(app => {
                  const job = app.job;
                  const companyName  = app.employer?.company_name || job?.company_name || '—';
                  const interestedOn = new Date(app.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  const s = app.status || 'interested';
                  const statusLabel = s === 'reviewing'  ? t('worker.dashboard.applications.statusReviewing')
                                    : s === 'rejected'   ? t('worker.dashboard.applications.statusNotSelected')
                                    : s === 'offer_sent' ? t('worker.dashboard.applications.statusOfferReceived')
                                    : s === 'hired'      ? t('worker.dashboard.applications.statusHired')
                                    : s === 'hire_fell_through' ? t('worker.dashboard.applications.statusFellThrough')
                                    : t('worker.dashboard.applications.statusInterested');
                  const statusColor = s === 'reviewing'  ? '#3B82F6'
                                    : s === 'rejected'   ? '#DC2626'
                                    : s === 'offer_sent' ? '#8B5CF6'
                                    : s === 'hired'      ? GREEN
                                    : s === 'hire_fell_through' ? '#6B7280'
                                    : AMBER;
                  const statusText     = statusColor === AMBER ? NAVY : WHITE;
                  const isExpanded     = expandedApp === app.id;
                  const hasOffer       = s === 'offer_sent';
                  const offer          = appOffers[app.id];
                  const latestOfferId  = app.latestOfferId || null;
                  const offerCount     = app.offerCount || 0;
                  const isSeen         = !latestOfferId || seenOffers.has(latestOfferId);
                  const isNewOffer     = hasOffer && !isSeen && offerCount <= 1;
                  const isUpdatedOffer = hasOffer && !isSeen && offerCount > 1;
                  const markOfferSeen  = () => {
                    if (latestOfferId && !isSeen) {
                      setSeenOffers(prev => {
                        const updated = new Set([...prev, latestOfferId]);
                        localStorage.setItem('seenOffers', JSON.stringify([...updated]));
                        return updated;
                      });
                    }
                  };
                  const cardBorder = isNewOffer
                    ? '2px solid rgba(245,166,35,0.6)'
                    : isUpdatedOffer ? '2px solid rgba(59,130,246,0.5)'
                    : hasOffer ? '1px solid rgba(139,92,246,0.4)'
                    : '1px solid rgba(255,255,255,0.07)';
                  const isNotifTarget = highlightAppId === app.id;
                  return (
                    <div
                      key={app.id}
                      id={`application-${app.id}`}
                      style={{
                        background: NAVY_MID, border: cardBorder, borderRadius: '10px', overflow: 'hidden',
                        animation: isNewOffer ? 'pulse-border 2s infinite' : 'none',
                        boxShadow: isNotifTarget ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s',
                      }}
                    >
                      {/* Offer banner */}
                      {hasOffer && (
                        <div style={{
                          backgroundColor: isUpdatedOffer ? 'rgba(59,130,246,0.12)' : 'rgba(245,166,35,0.12)',
                          borderBottom: isUpdatedOffer ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(245,166,35,0.25)',
                          padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                          <span style={{ fontSize: '13px' }}>{isUpdatedOffer ? '🔄' : '🎉'}</span>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: isUpdatedOffer ? '#3B82F6' : AMBER }}>
                            {isNewOffer ? t('worker.dashboard.applications.newJobOffer') : isUpdatedOffer ? t('worker.dashboard.applications.updatedOffer') : t('worker.dashboard.applications.youHaveOffer')}
                          </span>
                          <button
                            onClick={() => {
                              const next = isExpanded ? null : app.id;
                              setExpandedApp(next);
                              if (next) { handleLoadOffer(app.id, app.status); markOfferSeen(); }
                            }}
                            style={{ marginLeft: 'auto', background: isUpdatedOffer ? '#3B82F6' : AMBER, border: 'none', color: isUpdatedOffer ? WHITE : NAVY, fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                          >{isExpanded ? t('worker.dashboard.applications.hideDetails') : t('worker.dashboard.applications.viewOffer')}</button>
                        </div>
                      )}

                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: job ? WHITE : 'rgba(255,255,255,0.4)', fontStyle: job ? 'normal' : 'italic', marginBottom: '4px' }}>
                              {job ? job.job_title : t('worker.dashboard.applications.jobNoLongerAvailable')}
                            </div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginBottom: '6px' }}>{companyName}{job?.city ? ` · ${job.city}, ${job.state}` : ''}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              {(job?.salary_min || job?.salary_max) && (
                                <span style={{ fontSize: '12px', color: AMBER, fontWeight: 600 }}>${job.salary_min}–${job.salary_max}/hr</span>
                              )}
                              {job?.job_type && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{job.job_type}</span>}
                            </div>
                          </div>
                          <span style={{ backgroundColor: statusColor, color: statusText, borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px', flexShrink: 0 }}>
                            {statusLabel}
                          </span>
                        </div>
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.28)' }}>{t('worker.dashboard.applications.interestedOn', { date: interestedOn })}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {app.worker_archived_at ? (
                              <button
                                onClick={() => handleUnarchiveApplication(app.id)}
                                style={{ background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '10px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                              >{t('worker.dashboard.applications.unarchive')}</button>
                            ) : (
                              (['hired', 'rejected', 'not_selected', 'hire_fell_through'].includes(s) || !job) && (
                                <button
                                  onClick={() => handleArchiveApplication(app.id)}
                                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.45)', fontSize: '10px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                                >{t('worker.dashboard.applications.archive')}</button>
                              )
                            )}
                            <button
                              onClick={() => handleDeleteApplication(app.id)}
                              style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', fontSize: '10px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                            >{t('worker.dashboard.applications.delete')}</button>
                            {job && (
                              <button
                                onClick={() => setExpandedJobDetail(expandedJobDetail === app.id ? null : app.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: AMBER, fontFamily: FONT_BODY, padding: 0 }}
                              >
                                {expandedJobDetail === app.id ? t('worker.dashboard.applications.hideJobDetails') : t('worker.dashboard.applications.viewJobDetails')}
                              </button>
                            )}
                            {/* In the actions row rather than beside the title, where its label
                                squeezed the title column to a sliver on phones. */}
                            {app.hasReceivedOffer && (
                              <CompareToggle
                                selected={offerCompare.isSelected(app.id)}
                                onToggle={() => offerCompare.toggle(app.id)}
                                addLabel={t('compare.addToCompare')}
                                removeLabel={t('compare.removeFromCompare')}
                              />
                            )}
                          </div>
                        </div>

                        {expandedJobDetail === app.id && (
                          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                            {job?.trade_category && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.tradeLabel')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.trade_category}</span>
                              </div>
                            )}
                            {job?.job_type && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.jobTypeLabel')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.job_type}</span>
                              </div>
                            )}
                            {job?.min_years_experience && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.experienceRequiredLabel')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.min_years_experience}</span>
                              </div>
                            )}
                            {job?.union_status && (
                              <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.unionStatusLabel')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.union_status}</span>
                              </div>
                            )}
                            {(job?.shift || job?.urgently_hiring || job?.willing_to_train) && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                {job.shift && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{job.shift}</span>
                                )}
                                {job.urgently_hiring && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: '#E24B4A', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.urgentlyHiring')}</span>
                                )}
                                {job.willing_to_train && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, border: `1px solid ${AMBER}`, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.willingToTrain')}</span>
                                )}
                              </div>
                            )}
                            {job?.per_diem && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.perDiem')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.per_diem_amount || t('common.yes')}</span>
                              </div>
                            )}
                            {job?.sign_on_bonus_amount && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.signOnBonus')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>${job.sign_on_bonus_amount}</span>
                              </div>
                            )}
                            {job?.travel_required && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.travelLabel')}</span>
                                <span style={{ fontSize: '13px', color: WHITE }}>{job.travel_details || t('worker.dashboard.jobs.requiredFallback')}</span>
                              </div>
                            )}
                            {Array.isArray(job?.benefits) && job.benefits.length > 0 && (
                              <div style={{ marginBottom: '12px' }}>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.benefitsLabel')}</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {job.benefits.map(b => (
                                    <span key={b} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {job?.job_description && (
                              <div>
                                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.jobDescriptionLabel')}</span>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{renderJobDescription(job.job_description)}</div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Expanded offer details */}
                        {isExpanded && hasOffer && (
                          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                            {offerLoading === app.id ? (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '12px 0' }}>{t('worker.dashboard.applications.loadingOfferDetails')}</div>
                            ) : offer ? (
                              <>
                                {/* Company logo / name header */}
                                {app.employer?.logo_url ? (
                                  <div style={{ marginBottom: '14px' }}>
                                    <ZoomableImg src={app.employer.logo_url} alt={app.employer?.company_name || ''} style={{ height: '60px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                                  </div>
                                ) : app.employer?.company_name ? (
                                  <div style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: AMBER, letterSpacing: '0.06em', marginBottom: '10px' }}>{app.employer.company_name}</div>
                                ) : null}

                                {/* Which specific person sent this offer, distinct from the company identity above */}
                                {offerSenders[app.id]?.display_name && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                    <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', backgroundColor: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {offerSenders[app.id].avatar_url
                                        ? <ZoomableImg src={offerSenders[app.id].avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        : <span style={{ fontSize: '10px', fontWeight: 700, color: NAVY }}>{offerSenders[app.id].display_name[0]}</span>}
                                    </div>
                                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>
                                      {t('worker.dashboard.applications.offerSentByPrefix')}{offerSenders[app.id].display_name}
                                    </span>
                                  </div>
                                )}

                                {/* Job title */}
                                {offer.job_title && (
                                  <div style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '14px' }}>{offer.job_title}</div>
                                )}

                                {/* Pay */}
                                {offer.offered_pay && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px' }}>{t('worker.dashboard.applications.payLabel')}</span>
                                    <span style={{ fontSize: '15px', fontWeight: 700, color: AMBER }}>{offer.offered_pay}</span>
                                    {offer.pay_schedule && <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginLeft: '6px' }}>· {offer.pay_schedule}</span>}
                                  </div>
                                )}

                                {/* Start date */}
                                {offer.start_date && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px' }}>{t('worker.dashboard.applications.startDateLabel')}</span>
                                    <span style={{ fontSize: '13px', color: WHITE }}>{parseExamDate(offer.start_date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                  </div>
                                )}

                                {/* Work location */}
                                {offer.work_location && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px' }}>{t('worker.dashboard.applications.workLocationLabel')}</span>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: WHITE, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '6px', padding: '3px 10px' }}>{offer.work_location}</span>
                                  </div>
                                )}

                                {/* Work hours */}
                                {offer.work_hours && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px' }}>{t('worker.dashboard.applications.workHoursLabel')}</span>
                                    <span style={{ fontSize: '13px', color: WHITE }}>{offer.work_hours}</span>
                                  </div>
                                )}

                                {/* Overtime availability */}
                                {offer.overtime_availability && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px' }}>{t('worker.dashboard.applications.overtimeLabel')}</span>
                                    <span style={{ fontSize: '13px', color: WHITE }}>{offer.overtime_availability}</span>
                                  </div>
                                )}

                                {/* Reporting manager */}
                                {offer.reporting_manager && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px' }}>{t('worker.dashboard.applications.reportingManagerLabel')}</span>
                                    <span style={{ fontSize: '13px', color: WHITE }}>{offer.reporting_manager}</span>
                                  </div>
                                )}

                                {/* Benefits */}
                                {offer.benefits && offer.benefits.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '6px' }}>{t('worker.dashboard.applications.benefitsLabel')}</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {offer.benefits.map(b => (
                                        <span key={b} style={{ fontSize: '11px', fontWeight: 600, color: NAVY, backgroundColor: AMBER, borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Personal message */}
                                {offer.message && (
                                  <div style={{ marginBottom: '14px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '6px' }}>{t('worker.dashboard.applications.messageFromEmployerLabel')}</span>
                                    <div style={{ borderLeft: `3px solid ${AMBER}`, paddingLeft: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{offer.message}</div>
                                  </div>
                                )}

                                {/* First day instructions */}
                                {offer.first_day_instructions && (
                                  <div style={{ marginBottom: '14px', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px 16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                                      <span style={{ fontSize: '16px' }}>📋</span>
                                      <span style={{ fontFamily: FONT_HEAD, fontSize: '1rem', color: WHITE, letterSpacing: '0.06em' }}>{t('worker.dashboard.applications.firstDayInstructionsLabel')}</span>
                                    </div>
                                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{offer.first_day_instructions}</p>
                                  </div>
                                )}

                                {/* Documents */}
                                {offer.attached_documents && offer.attached_documents.length > 0 && (
                                  <div style={{ marginBottom: '14px' }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '7px' }}>{t('worker.dashboard.applications.documentsLabel')}</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      {offer.attached_documents.map((url, i) => {
                                        const filename = decodeURIComponent(url.split('/').pop() || t('worker.dashboard.applications.documentFallback', { num: i + 1 }));
                                        return (
                                          <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: NAVY, border: '1px solid rgba(255,255,255,0.2)', color: WHITE, fontFamily: FONT_BODY, fontSize: '12px', fontWeight: 600, padding: '8px 16px', borderRadius: '8px', textDecoration: 'none', alignSelf: 'flex-start' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                            </svg>
                                            {filename}
                                          </a>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Accept / Negotiate / Decline */}
                                <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                                  <button onClick={() => { setAcceptConfirmModal({ app, offer }); setAcceptConfirmChecked(false); }}
                                    style={{ background: '#10B981', border: 'none', color: WHITE, fontFamily: FONT_BODY, fontSize: '12px', fontWeight: 700, padding: '9px 20px', borderRadius: '8px', cursor: 'pointer' }}>
                                    {t('worker.dashboard.applications.acceptOffer')}
                                  </button>
                                  <button onClick={async () => {
                                    const convId = await startConversation(supabase, worker.id, offer.employer_id, offer.job_id, 'worker');
                                    if (!convId) return;
                                    setOpenConversationId(convId);
                                    setActiveTab('Messages');
                                  }}
                                    style={{ background: 'transparent', border: '1.5px solid #F5A623', color: '#F5A623', fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>
                                    {t('worker.dashboard.applications.negotiate')}
                                  </button>
                                  <button onClick={() => handleDeclineOffer(app, offer)}
                                    style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.4)', color: '#F87171', fontFamily: FONT_BODY, fontSize: '12px', fontWeight: 600, padding: '9px 20px', borderRadius: '8px', cursor: 'pointer' }}>
                                    {t('worker.dashboard.applications.declineOffer')}
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '12px 0' }}>{t('worker.dashboard.applications.offerDetailsUnavailable')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            })()}
          </div>
        )}

        {/* ── Saved Jobs ── */}
        {activeTab === 'Saved Jobs' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('worker.dashboard.savedJobs.header')}
            </p>
            {loadingSaved ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px', marginTop: '32px' }}>{t('common.loading')}</div>
            ) : savedJobs.length === 0 ? (
              <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '32px 20px', textAlign: 'center' }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginBottom: '16px' }}>{t('worker.dashboard.savedJobs.noSavedJobs')}</p>
                <button onClick={() => setActiveTab('Dashboard')} style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '13px', fontWeight: 700, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}>
                  {t('worker.dashboard.applications.browseJobs')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {savedJobs.map(job => (
                  <div key={job.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: WHITE, marginBottom: '4px' }}>{job.job_title}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
                      <span className="company-link" onClick={() => navigate(`/company/${job.employer_id}`)} style={{ cursor: 'pointer', color: AMBER, fontSize: '12px', fontWeight: 600 }}>{job.company_name}</span>
                      {joinParts([job.city, job.state], ', ') && <>{' '}· {joinParts([job.city, job.state], ', ')}</>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '11px', color: AMBER, fontWeight: 600 }}>${job.salary_min}–${job.salary_max}/hr</span>
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{joinParts([job.job_type, job.union_status])}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={e => handleInterested(job, e)}
                        style={{ background: appliedJobs.includes(job.id) ? GREEN : AMBER, border: 'none', color: appliedJobs.includes(job.id) ? WHITE : NAVY, fontSize: '11px', fontWeight: 600, padding: '7px 14px', borderRadius: '6px', cursor: 'pointer' }}
                      >{appliedJobs.includes(job.id) ? t('worker.dashboard.savedJobs.interestedDone') : t('worker.dashboard.savedJobs.interested')}</button>
                      <button
                        onClick={e => handleRemoveSaved(job.id, e)}
                        style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.35)', color: '#F87171', fontSize: '11px', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer' }}
                      >{t('worker.dashboard.savedJobs.remove')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Apprenticeships ── */}
        {activeTab === 'Apprenticeships' && (() => {
          const q = apprenticeshipSearch.trim().toLowerCase();
          const workerTrade = (trades?.trade || '').toLowerCase();
          const filtered = apprenticeships.filter(a => {
            if (!q) return true;
            return (a.title || '').toLowerCase().includes(q) || (a.trade || '').toLowerCase().includes(q);
          });
          const sorted = [...filtered].sort((a, b) => {
            const aMatch = workerTrade && (a.trade || '').toLowerCase() === workerTrade ? 0 : 1;
            const bMatch = workerTrade && (b.trade || '').toLowerCase() === workerTrade ? 0 : 1;
            return aMatch - bMatch;
          });
          return (
            <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>
                {t('nav.worker.apprenticeships')}
              </p>

              <div style={{ marginBottom: '14px' }}>
                <CompareEntryButton
                  count={apprCompare.ids.length}
                  label={t('compare.worker.apprenticeships.entryButton')}
                  onClick={() => setOpenCompare('apprenticeships')}
                />
              </div>

              <div style={{ position: 'relative', marginBottom: '14px' }}>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                >
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={apprenticeshipSearch}
                  onChange={e => setApprenticeshipSearch(e.target.value)}
                  placeholder={t('worker.dashboard.apprenticeships.searchPlaceholder')}
                  style={{
                    width: '100%', padding: '9px 32px', fontSize: '12px', fontFamily: FONT_BODY,
                    background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px', color: WHITE, outline: 'none',
                  }}
                />
              </div>

              {!apprenticeshipsLoaded ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px', marginTop: '32px' }}>{t('common.loading')}</div>
              ) : sorted.length === 0 ? (
                <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '32px 20px', textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                    {apprenticeships.length === 0 ? t('worker.dashboard.apprenticeships.noneOpen') : t('worker.dashboard.apprenticeships.noneMatchSearch', { query: apprenticeshipSearch })}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {sorted.map(appr => {
                    const apprStatus = apprenticeshipStatusMap[appr.id];
                    const statusBadge = apprStatus ? APPR_STATUS_BADGE[apprStatus] : null;
                    const apprExams = apprExamsMap[appr.id] || [];
                    const hasRequirements = (appr.requirements || []).length > 0;
                    const exploreProps = {
                      workerId: worker?.id,
                      appliedApprenticeshipIds: Object.keys(apprenticeshipStatusMap),
                      jobVisible: job => matchesScheduleFilter(job, trades?.availability) && matchesRadiusFilter(job, worker?.zip_code, profile?.commute_radius_miles),
                      onViewApprenticeship: id => { setApprenticeshipSearch(''); setHighlightApprId(id); },
                      onViewJob: id => {
                        setAllJobsSearch(''); setAllJobsTradeFilter('all'); setAllJobsTypeFilter('all'); setAllJobsCityFilter('all');
                        setHighlightJobId(id); setActiveTab('Jobs');
                      },
                      onSeeAllJobs: names => {
                        setAllJobsSearch(''); setAllJobsTypeFilter('all'); setAllJobsCityFilter('all');
                        setAllJobsTradeGroup(names); setAllJobsTradeFilter('__group'); setActiveTab('Jobs');
                      },
                    };

                    // The worker's own application to a posting that is closed or archived: a
                    // reduced card from my_applied_apprenticeship_postings(). It keeps its title,
                    // union and status (and the same element id, so a rejection notification
                    // still lands on it) but offers nothing to apply to or compare.
                    if (appr.noLongerOpen) {
                      return (
                        <div
                          key={appr.id}
                          id={`apprenticeship-${appr.id}`}
                          data-no-longer-open="true"
                          style={{
                            background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '14px 16px',
                            boxShadow: highlightApprId === appr.id ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s',
                          }}
                        >
                          {appr.union_name && <div style={{ fontSize: '12px', fontWeight: 700, color: WHITE, marginBottom: '2px', overflowWrap: 'anywhere' }}>{appr.union_name}</div>}
                          <div style={{ fontSize: '13px', fontWeight: 600, color: WHITE, marginBottom: '6px', overflowWrap: 'anywhere' }}>
                            {appr.title || t('worker.dashboard.apprenticeships.postingUnavailableTitle')}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                            {appr.trade && (
                              <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>{appr.trade}</span>
                            )}
                            <span data-testid="posting-closed-badge" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>
                              {t('worker.dashboard.apprenticeships.closedBadge')}
                            </span>
                          </div>
                          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: '10px' }}>
                            {t(appr.detailsUnavailable ? 'worker.dashboard.apprenticeships.postingDetailsUnavailable' : 'worker.dashboard.apprenticeships.noLongerOpen')}
                          </p>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {appr.union_id && (
                              <button
                                onClick={() => navigate(getUnionProfileUrl(appr))}
                                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, padding: '7px 14px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('worker.dashboard.apprenticeships.viewUnionProfile')}</button>
                            )}
                            {statusBadge && (
                              <span style={{
                                backgroundColor: statusBadge.bg, color: statusBadge.color,
                                borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>{t(statusBadge.labelKey)}</span>
                            )}
                          </div>
                          {apprStatus === 'rejected' && worker && appr.trade && (
                            <ExploreOtherOptions apprenticeship={appr} {...exploreProps} />
                          )}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={appr.id}
                        id={`apprenticeship-${appr.id}`}
                        style={{
                          background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '14px 16px',
                          boxShadow: highlightApprId === appr.id ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: WHITE, marginBottom: '2px' }}>{appr.union_name}</div>
                          <CompareToggle
                            selected={apprCompare.isSelected(appr.id)}
                            onToggle={() => apprCompare.toggle(appr.id)}
                            addLabel={t('compare.addToCompare')}
                            removeLabel={t('compare.removeFromCompare')}
                          />
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: WHITE, marginBottom: '6px' }}>{appr.title}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                          {appr.trade && (
                            <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>{appr.trade}</span>
                          )}
                          {appr.gi_bill_eligible && (
                            <span style={{ backgroundColor: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>{t('marketing.trades.giBillEligible')}</span>
                          )}
                          {appr.drug_screening_required && (
                            <span data-testid="drug-screening-badge" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: WHITE, border: '1px solid rgba(255,255,255,0.28)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>{t('marketing.trades.drugScreeningRequired')}</span>
                          )}
                          {apprExams.length > 0 && (
                            <span style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: WHITE, border: '1px solid rgba(245,166,35,0.4)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>{t('exams.worker.examRequiredBadge', { count: apprExams.length })}</span>
                          )}
                          {(appr.city || appr.state) && (
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{appr.city}{appr.city && appr.state ? ', ' : ''}{appr.state}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px', flexWrap: 'wrap' }}>
                          {appr.duration && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                              {t('worker.dashboard.apprenticeships.lengthPrefix', { duration: appr.duration })}
                            </span>
                          )}
                          {appr.hourly_rate_start && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: AMBER, fontWeight: 600 }}>
                              <IconDollar color={AMBER} /> ${appr.hourly_rate_start}{appr.hourly_rate_end ? ` - $${appr.hourly_rate_end}` : ''}/hr
                            </span>
                          )}
                        </div>
                        {(appr.description || hasRequirements || apprExams.length > 0) && (
                          <button
                            onClick={() => setExpandedAppr(expandedAppr === appr.id ? null : appr.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: AMBER, fontFamily: FONT_BODY, padding: '0 0 12px', display: 'block' }}
                          >
                            {expandedAppr === appr.id ? t('worker.dashboard.apprenticeships.hideDetails') : t('worker.dashboard.apprenticeships.viewDetails')}
                          </button>
                        )}
                        {expandedAppr === appr.id && (
                          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                            {appr.description && (
                              <div style={{ marginBottom: (hasRequirements || apprExams.length > 0) ? '14px' : 0 }}>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.apprenticeships.descriptionLabel')}</div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{renderJobDescription(appr.description)}</div>
                              </div>
                            )}
                            <ApprenticeshipPrerequisites exams={apprExams} requirements={appr.requirements} />
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <ApprenticeshipResourceLink apprenticeship={appr} />
                          <button
                            onClick={() => navigate(getUnionProfileUrl(appr))}
                            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 600, padding: '7px 14px', borderRadius: '6px', cursor: 'pointer' }}
                          >{t('worker.dashboard.apprenticeships.viewUnionProfile')}</button>
                          <button
                            onClick={() => handleApplyApprenticeship(appr)}
                            disabled={!!apprStatus || applyingApprenticeshipId === appr.id}
                            style={{
                              background: apprStatus ? 'rgba(255,255,255,0.12)' : AMBER,
                              border: 'none',
                              color: apprStatus ? 'rgba(255,255,255,0.5)' : NAVY,
                              fontSize: '11px', fontWeight: 600,
                              padding: '7px 14px', borderRadius: '6px',
                              cursor: apprStatus ? 'default' : 'pointer',
                              opacity: applyingApprenticeshipId === appr.id ? 0.7 : 1,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >{apprStatus ? t('worker.dashboard.savedJobs.interestedDone') : applyingApprenticeshipId === appr.id ? t('worker.dashboard.apprenticeships.applying') : t('worker.dashboard.apprenticeships.interestedArrow')}</button>
                          {statusBadge && (
                            <span style={{
                              backgroundColor: statusBadge.bg, color: statusBadge.color,
                              borderRadius: '999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>{t(statusBadge.labelKey)}</span>
                          )}
                        </div>
                        {apprApplyMsg[appr.id] && (
                          <p
                            role={apprApplyMsg[appr.id].kind === 'error' ? 'alert' : 'status'}
                            data-testid="appr-apply-message"
                            data-kind={apprApplyMsg[appr.id].kind}
                            style={{ marginTop: '8px', fontSize: '12px', fontWeight: 600, lineHeight: 1.45, overflowWrap: 'anywhere', color: apprApplyMsg[appr.id].kind === 'error' ? '#F09595' : '#5DCAA5' }}
                          >
                            {apprApplyMsg[appr.id].text}
                          </p>
                        )}
                        {apprStatus === 'rejected' && worker && (
                          <ExploreOtherOptions apprenticeship={appr} {...exploreProps} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── All Jobs ── */}
        {activeTab === 'Jobs' && (() => {
          const q = allJobsSearch.trim().toLowerCase();
          const filtered = allJobs.filter(job => {
            const matchSearch = !q || (
              (job.job_title       || '').toLowerCase().includes(q) ||
              (job.company_name    || '').toLowerCase().includes(q) ||
              (job.job_description || '').toLowerCase().includes(q) ||
              (job.city            || '').toLowerCase().includes(q) ||
              (job.state           || '').toLowerCase().includes(q)
            );
            const matchTrade = allJobsTradeFilter === 'all'
              || (allJobsTradeFilter === '__group' ? allJobsTradeGroup.includes(job.trade_category) : job.trade_category === allJobsTradeFilter);
            const matchType  = allJobsTypeFilter  === 'all' || job.job_type === allJobsTypeFilter;
            const matchCity  = allJobsCityFilter  === 'all' || job.city === allJobsCityFilter;
            const matchSchedule = matchesScheduleFilter(job, trades?.availability);
            const matchRadius = matchesRadiusFilter(job, worker?.zip_code, profile?.commute_radius_miles);
            return matchSearch && matchTrade && matchType && matchCity && matchSchedule && matchRadius;
          });
          const uniqueTrades = ['all', ...Array.from(new Set(allJobs.map(j => j.trade_category).filter(Boolean)))];
          const uniqueTypes  = ['all', ...Array.from(new Set(allJobs.map(j => j.job_type).filter(Boolean)))];
          const uniqueCities = Array.from(new Set(allJobs.map(j => j.city).filter(Boolean))).sort();
          return (
            <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>
                {t('worker.dashboard.jobs.header')}
              </p>

              <div style={{ marginBottom: '12px' }}>
                <CompareEntryButton
                  count={jobCompare.ids.length}
                  label={t('compare.worker.jobs.entryButton')}
                  onClick={() => setOpenCompare('jobs')}
                />
              </div>

              {/* Search + filters */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                {/* Search bar */}
                <div style={{ position: 'relative' }}>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  >
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    value={allJobsSearch}
                    onChange={e => setAllJobsSearch(e.target.value)}
                    placeholder={t('worker.dashboard.jobs.searchPlaceholder')}
                    style={{
                      width: '100%', padding: '9px 32px 9px 32px',
                      fontSize: '12px', fontFamily: FONT_BODY,
                      background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px', color: WHITE, outline: 'none',
                    }}
                  />
                  {allJobsSearch && (
                    <button
                      onClick={() => { setAllJobsSearch(''); setAllJobsCityFilter('all'); }}
                      style={{
                        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                        color: 'rgba(255,255,255,0.4)', fontSize: '14px', lineHeight: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      aria-label={t('worker.dashboard.jobs.clearSearchLabel')}
                    >✕</button>
                  )}
                </div>

                {/* Trade + type + city dropdowns -- option lists themselves are DB-sourced
                    (employer-entered trade_category/job_type/city values), left untranslated */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={allJobsTradeFilter}
                    onChange={e => setAllJobsTradeFilter(e.target.value)}
                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: '12px', fontFamily: FONT_BODY, background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: allJobsTradeFilter === 'all' ? 'rgba(255,255,255,0.4)' : WHITE, outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="all">{t('worker.dashboard.jobs.allTrades')}</option>
                    {allJobsTradeFilter === '__group' && <option value="__group">{allJobsTradeGroup.join(' / ')}</option>}
                    {uniqueTrades.filter(t => t !== 'all').map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select
                    value={allJobsTypeFilter}
                    onChange={e => setAllJobsTypeFilter(e.target.value)}
                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: '12px', fontFamily: FONT_BODY, background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: allJobsTypeFilter === 'all' ? 'rgba(255,255,255,0.4)' : WHITE, outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="all">{t('worker.dashboard.jobs.allTypes')}</option>
                    {uniqueTypes.filter(t => t !== 'all').map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select
                    value={allJobsCityFilter}
                    onChange={e => setAllJobsCityFilter(e.target.value)}
                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: '12px', fontFamily: FONT_BODY, background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: allJobsCityFilter === 'all' ? 'rgba(255,255,255,0.4)' : WHITE, outline: 'none', cursor: 'pointer' }}
                  >
                    <option value="all">{t('worker.dashboard.jobs.allCities')}</option>
                    {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Results count */}
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px', fontFamily: FONT_BODY }}>
                {t('worker.dashboard.jobs.showingCount', { filtered: filtered.length, count: allJobs.length })}
              </p>

              {/* Job list */}
              {!allJobsLoaded ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>{t('worker.dashboard.jobs.loadingJobs')}</div>
              ) : filtered.length === 0 ? (
                <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  {allJobs.length === 0
                    ? t('worker.dashboard.jobs.noneYetPosted')
                    : t('worker.dashboard.jobs.noneMatchSearch', { query: allJobsSearch })}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {filtered.map(job => {
                    const reqCerts = parseCertsRequired(job.certifications_required);
                    const certSet  = new Set(workerCertNames.map(n => n.toLowerCase().trim()));
                    const missingCerts = reqCerts.filter(c => !certSet.has(c.toLowerCase().trim()));
                    const reqLicenses = parseCertsRequired(job.licenses_required);
                    const licenseSet  = new Set(workerLicenseNames.map(n => n.toLowerCase().trim()));
                    const missingLicenses = reqLicenses.filter(l => !licenseSet.has(l.toLowerCase().trim()));
                    const missingRequirements = [...missingCerts, ...missingLicenses];
                    const hasMissing   = missingRequirements.length > 0;
                    const matchTier = computeMatchTier(job, trades?.trade, trades?.years_experience, workerCertNames, workerLicenseNames);
                    return (
                    <div key={job.id} id={`job-${job.id}`} data-testid="worker-job-card" style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderTop: job.featured ? `3px solid ${AMBER}` : '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px', cursor: 'pointer', position: 'relative', boxShadow: highlightJobId === job.id ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s' }} onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                      {job.featured && (
                        <span style={{ position: 'absolute', top: '8px', right: '10px', fontSize: '9px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, padding: '2px 7px', borderRadius: '4px', letterSpacing: '0.03em' }}>★ {t('worker.dashboard.jobs.featuredTag')}</span>
                      )}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: WHITE }}>{job.job_title}</span>
                            <MatchTierBadge tier={matchTier} t={t} />
                            {job.urgently_hiring && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: '#E24B4A', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.urgentlyHiring')}</span>
                            )}
                            {(job.number_of_openings || 1) > 1 && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: NAVY, backgroundColor: AMBER, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.hiringCount', { count: job.number_of_openings })}</span>
                            )}
                            {job.willing_to_train && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, border: `1px solid ${AMBER}`, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.willingToTrain')}</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
                            <span className="company-link" onClick={e => { e.stopPropagation(); navigate(`/company/${job.employer_id}`); }} style={{ cursor: 'pointer', color: AMBER, fontSize: '12px', fontWeight: 600 }}>{job.company_name}</span>
                            {joinParts([job.city, job.state], ', ') && <>{' '}· {joinParts([job.city, job.state], ', ')}</>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '11px', color: AMBER, fontWeight: 500 }}>${job.salary_min}–${job.salary_max}/hr</span>
                            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{joinParts([job.job_type, job.union_status])}</span>
                            {job.trade_category && (
                              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '2px 6px' }}>{job.trade_category}</span>
                            )}
                            {job.shift && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{job.shift}</span>
                            )}
                            {job.per_diem && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.perDiem')}</span>
                            )}
                            {job.sign_on_bonus_amount && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.signOnBonus')}</span>
                            )}
                            {job.travel_required && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.travelRequired')}</span>
                            )}
                            {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.benefitsCount', { count: job.benefits.length })}</span>
                            )}
                            {hasMissing && (
                              <span style={{ fontSize: '9px', color: NAVY, backgroundColor: AMBER, borderRadius: '4px', padding: '2px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('worker.dashboard.jobs.certificationRequired')}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '10px', flexShrink: 0 }}>
                          <CompareToggle
                            selected={jobCompare.isSelected(job.id)}
                            onToggle={() => jobCompare.toggle(job.id)}
                            addLabel={t('compare.addToCompare')}
                            removeLabel={t('compare.removeFromCompare')}
                          />
                          {!appliedJobs.includes(job.id) && (
                            <button
                              onClick={e => openDismissModal(job, e)}
                              title={t('worker.dashboard.jobs.notInterested')}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'rgba(255,255,255,0.3)', padding: '2px', fontFamily: FONT_BODY }}
                            >{t('worker.dashboard.jobs.notInterestedButton')}</button>
                          )}
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{expandedJob === job.id ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {expandedJob === job.id && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '16px' }}>
                          {(job.number_of_openings || 1) > 1 && (
                            <div style={{ fontSize: '13px', color: AMBER, fontWeight: 700, marginBottom: '12px' }}>
                              {t('worker.dashboard.jobs.openingsAvailable', { count: job.number_of_openings })}
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.tradeLabel')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.trade_category}</div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.jobTypeUnionStatusLabel')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{joinParts([job.job_type, job.union_status])}</div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.experienceRequiredLabel')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.min_years_experience}</div>
                          {reqCerts.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.certificationsRequiredLabel')}</div>
                              <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{reqCerts.join(', ')}</div>
                            </>
                          )}
                          {reqLicenses.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.licensesRequiredLabel')}</div>
                              <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{reqLicenses.join(', ')}</div>
                            </>
                          )}
                          {hasMissing && (
                            <div style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: AMBER, lineHeight: 1.5 }}>
                              {t('worker.dashboard.jobs.missingCertsWarning', { certs: missingRequirements.join(', ') })}
                            </div>
                          )}
                          {Array.isArray(job.certifications_preferred) && job.certifications_preferred.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.certificationsPreferredLabel')}</div>
                              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginBottom: '12px' }}>{job.certifications_preferred.join(', ')}</div>
                            </>
                          )}
                          {job.per_diem && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.perDiem')}</div>
                              <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.per_diem_amount || t('common.yes')}</div>
                            </>
                          )}
                          {job.sign_on_bonus_amount && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.signOnBonus')}</div>
                              <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>${job.sign_on_bonus_amount}</div>
                            </>
                          )}
                          {job.travel_required && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.travelLabel')}</div>
                              <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.travel_details || t('worker.dashboard.jobs.requiredFallback')}</div>
                            </>
                          )}
                          {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.benefitsLabel')}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                {job.benefits.map(b => (
                                  <span key={b} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                                ))}
                              </div>
                            </>
                          )}
                          {Array.isArray(job.physical_requirements) && job.physical_requirements.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.physicalRequirementsLabel')}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                {job.physical_requirements.map(r => (
                                  <span key={r} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{r}</span>
                                ))}
                              </div>
                            </>
                          )}
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.jobDescriptionLabel')}</div>
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', lineHeight: '1.6' }}>{renderJobDescription(job.job_description)}</div>
                          {renderCompanyInfo(job)}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {['hired', 'hire_fell_through'].includes(matchedJobStatusByJobId[job.id]) ? (
                              // A real, closed application record -- never offer the generic
                              // "remove interest" toggle here, it would delete it outright.
                              <span style={{
                                background: matchedJobStatusByJobId[job.id] === 'hired' ? GREEN : 'rgba(255,255,255,0.12)',
                                color: matchedJobStatusByJobId[job.id] === 'hired' ? WHITE : 'rgba(255,255,255,0.6)',
                                fontSize: '11px', fontWeight: 500,
                                padding: '8px 16px', borderRadius: '6px',
                              }}>
                                {matchedJobStatusByJobId[job.id] === 'hired' ? t('worker.dashboard.applications.statusHired') : t('worker.dashboard.applications.statusFellThrough')}
                              </span>
                            ) : (
                              <button
                                onClick={e => handleInterested(job, e)}
                                style={{
                                  background: appliedJobs.includes(job.id) ? GREEN : AMBER,
                                  border: 'none',
                                  color: appliedJobs.includes(job.id) ? WHITE : NAVY,
                                  fontSize: '11px', fontWeight: 500,
                                  padding: '8px 16px', borderRadius: '6px', cursor: 'pointer',
                                }}
                              >{appliedJobs.includes(job.id) ? t('worker.dashboard.savedJobs.interestedDone') : t('worker.dashboard.savedJobs.interested')}</button>
                            )}
                            <button
                              onClick={e => handleToggleSave(job, e)}
                              title={savedJobIds.has(job.id) ? t('worker.dashboard.jobs.unsaveJob') : t('worker.dashboard.jobs.saveJob')}
                              style={{
                                background: savedJobIds.has(job.id) ? AMBER : 'transparent',
                                border: `1px solid ${savedJobIds.has(job.id) ? AMBER : 'rgba(255,255,255,0.2)'}`,
                                color: savedJobIds.has(job.id) ? NAVY : 'rgba(255,255,255,0.45)',
                                fontSize: '13px', lineHeight: 1,
                                padding: '7px 10px', borderRadius: '6px', cursor: 'pointer',
                              }}
                            >🔖</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ); })}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'Dashboard' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>

            {/* Upcoming interview(s) -- mobile only, since the sidebar (where this also lives) is hidden below 768px */}
            {isMobile && upcomingInterviews.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {upcomingInterviews.map((interview) => {
                  const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
                  const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
                  return (
                    <div
                      key={interview.id}
                      onClick={() => openUpcomingInterview(interview)}
                      style={{ backgroundColor: NAVY_MID, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 10px 10px 0', padding: '12px 16px', cursor: 'pointer' }}
                    >
                      <p style={{ fontSize: '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {t('messaging.upcomingInterview')}
                      </p>
                      <p style={{ fontSize: '0.85rem', color: WHITE, fontWeight: 600, marginBottom: '2px' }}>
                        {interview.otherPartyName}
                      </p>
                      <p style={{ fontSize: '0.78rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                        {countdown.text}
                      </p>
                      {interview.status === 'accepted' && (
                        <p style={{ fontSize: '0.7rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}>{t('messaging.statusConfirmed')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {profileViewNotifs.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t('worker.dashboard.home.recentProfileViews')}</span>
                  <button onClick={handleClearAllNotifs} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AMBER, fontSize: '0.75rem', fontWeight: 600, fontFamily: FONT_BODY, padding: 0 }}>{t('common.clearAll')}</button>
                </div>
                {profileViewNotifs.map(notif => (
                  <div key={notif.id} style={{ borderLeft: `3px solid ${AMBER}`, backgroundColor: NAVY_MID, borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '1rem' }}>🏢</span>
                    <span style={{ flex: 1, fontSize: '0.84rem', color: WHITE, fontWeight: 500 }}>{t('worker.dashboard.home.viewedYourProfile', { company: notif.company_name })}</span>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)', flexShrink: 0 }}>{timeAgo(notif.viewed_at)}</span>
                    <button onClick={() => handleDismissNotif(notif.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', lineHeight: 1, padding: '0 0 0 4px', fontFamily: FONT_BODY, flexShrink: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '16px 18px' }}>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.38)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>{t('worker.dashboard.home.profileViewsLabel')}</p>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: WHITE, lineHeight: 1 }}>{profileViewCount}</p>
                <p style={{ fontSize: '0.78rem', color: GREEN, marginTop: '7px', fontWeight: 600 }}>{t('worker.dashboard.home.totalViews')}</p>
              </div>
              <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '16px 18px' }}>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.38)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>{t('nav.worker.matchedJobs')}</p>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: WHITE, lineHeight: 1 }}>{jobs.length}</p>
                {(() => {
                  const newTodayCount = jobs.filter(j => Date.now() - new Date(j.created_at).getTime() < 24 * 60 * 60 * 1000).length;
                  return newTodayCount > 0 ? (
                    <p style={{ fontSize: '0.78rem', color: AMBER, marginTop: '7px', fontWeight: 600 }}>{t('worker.dashboard.home.newToday', { count: newTodayCount })}</p>
                  ) : (
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', marginTop: '7px', fontWeight: 600 }}>{t('worker.dashboard.home.noneToday')}</p>
                  );
                })()}
              </div>
              <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '16px 18px' }}>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.38)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>{t('nav.worker.messages')}</p>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: WHITE, lineHeight: 1 }}>{unreadCount || 0}</p>
                <p style={{ fontSize: '0.78rem', color: unreadCount > 0 ? AMBER : 'rgba(255,255,255,0.3)', marginTop: '7px', fontWeight: 600 }}>{unreadCount > 0 ? t('worker.dashboard.home.unread') : t('worker.dashboard.home.none')}</p>
              </div>
            </div>

            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {trades?.trade ? t('worker.dashboard.home.matchedJobsForYouTrade', { trade: trades.trade }) : t('worker.dashboard.home.matchedJobsForYou')}
            </p>

            {/* Search bar */}
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', flexShrink: 0 }}
              >
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                value={jobSearch}
                onChange={e => setJobSearch(e.target.value)}
                placeholder={t('worker.dashboard.home.searchJobsPlaceholder')}
                style={{
                  width: '100%', padding: '9px 32px 9px 32px',
                  fontSize: '12px', fontFamily: FONT_BODY,
                  background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', color: WHITE, outline: 'none',
                }}
              />
              {jobSearch && (
                <button
                  onClick={() => setJobSearch('')}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                    color: 'rgba(255,255,255,0.4)', fontSize: '14px', lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label={t('worker.dashboard.jobs.clearSearchLabel')}
                >✕</button>
              )}
            </div>

            {/* Results count */}
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px', fontFamily: FONT_BODY }}>
              {t('worker.dashboard.jobs.showingCount', { filtered: filteredJobs.length, count: jobs.length })}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {jobs.length === 0 ? (
                trades?.trade ? (
                  <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                    {t('worker.dashboard.home.noJobsForTrade', { trade: trades.trade })}
                  </div>
                ) : (
                  // No trade is why nothing matched -- say that, not "no jobs have been posted yet".
                  <div data-testid="matched-needs-trade" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: '8px', padding: '16px', textAlign: 'center', fontSize: '13px', color: AMBER }}>
                    {t('worker.dashboard.home.completeProfileForMatches')}
                  </div>
                )
              ) : filteredJobs.length === 0 ? (
                <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  {t('worker.dashboard.jobs.noneMatchSearch', { query: jobSearch })}
                </div>
              ) : (
                filteredJobs.map(job => {
                  const matchedJobStatus = matchedJobStatusByJobId[job.id];
                  const matchedJobStatusLabel = matchedJobStatus === 'reviewing'  ? t('worker.dashboard.home.statusUnderReview')
                                              : matchedJobStatus === 'offer_sent' ? t('worker.dashboard.applications.statusOfferReceived')
                                              : matchedJobStatus ? t('worker.dashboard.home.statusApplied')
                                              : null;
                  const matchedJobStatusColor = matchedJobStatus === 'reviewing'  ? '#3B82F6'
                                              : matchedJobStatus === 'offer_sent' ? '#8B5CF6'
                                              : AMBER;
                  const matchTier = computeMatchTier(job, trades?.trade, trades?.years_experience, workerCertNames, workerLicenseNames);
                  return (
                  <div key={job.id} data-testid="worker-job-card" style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderTop: job.featured ? `3px solid ${AMBER}` : '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px', cursor: 'pointer', position: 'relative' }} onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                    {job.featured && (
                      <span style={{ position: 'absolute', top: '8px', right: '10px', fontSize: '9px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, padding: '2px 7px', borderRadius: '4px', letterSpacing: '0.03em' }}>★ {t('worker.dashboard.jobs.featuredTag')}</span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '500', color: 'white' }}>{job.job_title}</span>
                          <MatchTierBadge tier={matchTier} t={t} />
                          {job.urgently_hiring && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: '#E24B4A', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.urgentlyHiring')}</span>
                          )}
                          {(job.number_of_openings || 1) > 1 && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: NAVY, backgroundColor: AMBER, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.hiringCount', { count: job.number_of_openings })}</span>
                          )}
                          {job.willing_to_train && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, border: `1px solid ${AMBER}`, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.willingToTrain')}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
                          <span className="company-link" onClick={e => { e.stopPropagation(); navigate(`/company/${job.employer_id}`); }} style={{ cursor: 'pointer', color: AMBER, fontSize: '12px', fontWeight: 600 }}>{job.company_name}</span>
                          {joinParts([job.city, job.state], ', ') && <>{' '}· {joinParts([job.city, job.state], ', ')}</>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', color: AMBER, fontWeight: '500' }}>${job.salary_min}–${job.salary_max}/hr</span>
                          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{joinParts([job.job_type, job.union_status])}</span>
                          {job.shift && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{job.shift}</span>
                          )}
                          {job.per_diem && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.perDiem')}</span>
                          )}
                          {job.sign_on_bonus_amount && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.signOnBonus')}</span>
                          )}
                          {job.travel_required && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.travelRequired')}</span>
                          )}
                          {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                            <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.benefitsCount', { count: job.benefits.length })}</span>
                          )}
                          {matchedJobStatusLabel && (
                            <span style={{ backgroundColor: matchedJobStatusColor, color: matchedJobStatusColor === AMBER ? NAVY : WHITE, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>
                              {matchedJobStatusLabel}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '10px', flexShrink: 0 }}>
                        {!matchedJobStatus && (
                          <button
                            onClick={e => openDismissModal(job, e)}
                            title={t('worker.dashboard.jobs.notInterested')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'rgba(255,255,255,0.3)', padding: '2px', fontFamily: FONT_BODY }}
                          >{t('worker.dashboard.jobs.notInterestedButton')}</button>
                        )}
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{expandedJob === job.id ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {expandedJob === job.id && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '16px' }}>
                        {(job.number_of_openings || 1) > 1 && (
                          <div style={{ fontSize: '13px', color: AMBER, fontWeight: 700, marginBottom: '12px' }}>
                            {t('worker.dashboard.jobs.openingsAvailable', { count: job.number_of_openings })}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.tradeLabel')}</div>
                        <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.trade_category}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.jobTypeUnionStatusLabel')}</div>
                        <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{joinParts([job.job_type, job.union_status])}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.experienceRequiredLabel')}</div>
                        <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.min_years_experience}</div>
                        {parseCertsRequired(job.certifications_required).length > 0 && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.certificationsRequiredLabel')}</div>
                            <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{parseCertsRequired(job.certifications_required).join(', ')}</div>
                          </>
                        )}
                        {parseCertsRequired(job.licenses_required).length > 0 && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.licensesRequiredLabel')}</div>
                            <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{parseCertsRequired(job.licenses_required).join(', ')}</div>
                          </>
                        )}
                        {Array.isArray(job.certifications_preferred) && job.certifications_preferred.length > 0 && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.certificationsPreferredLabel')}</div>
                            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginBottom: '12px' }}>{job.certifications_preferred.join(', ')}</div>
                          </>
                        )}
                        {job.per_diem && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.perDiem')}</div>
                            <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.per_diem_amount || t('common.yes')}</div>
                          </>
                        )}
                        {job.sign_on_bonus_amount && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.signOnBonus')}</div>
                            <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>${job.sign_on_bonus_amount}</div>
                          </>
                        )}
                        {job.travel_required && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.travelLabel')}</div>
                            <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.travel_details || t('worker.dashboard.jobs.requiredFallback')}</div>
                          </>
                        )}
                        {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.benefitsLabel')}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                              {job.benefits.map(b => (
                                <span key={b} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                              ))}
                            </div>
                          </>
                        )}
                        {Array.isArray(job.physical_requirements) && job.physical_requirements.length > 0 && (
                          <>
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.physicalRequirementsLabel')}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                              {job.physical_requirements.map(r => (
                                <span key={r} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{r}</span>
                              ))}
                            </div>
                          </>
                        )}
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.jobDescriptionLabel')}</div>
                        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{job.job_description}</div>
                        {renderCompanyInfo(job)}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={e => handleInterested(job, e)}
                            style={{
                              background: appliedJobs.includes(job.id) ? GREEN : AMBER,
                              border: 'none',
                              color: appliedJobs.includes(job.id) ? WHITE : NAVY,
                              fontSize: '11px', fontWeight: '500',
                              padding: '8px 16px', borderRadius: '6px', cursor: 'pointer',
                            }}
                          >{appliedJobs.includes(job.id) ? t('worker.dashboard.savedJobs.interestedDone') : t('worker.dashboard.savedJobs.interested')}</button>
                          <button
                            onClick={e => handleToggleSave(job, e)}
                            title={savedJobIds.has(job.id) ? t('worker.dashboard.jobs.unsaveJob') : t('worker.dashboard.jobs.saveJob')}
                            style={{
                              background: savedJobIds.has(job.id) ? AMBER : 'transparent',
                              border: `1px solid ${savedJobIds.has(job.id) ? AMBER : 'rgba(255,255,255,0.2)'}`,
                              color: savedJobIds.has(job.id) ? NAVY : 'rgba(255,255,255,0.45)',
                              fontSize: '13px', lineHeight: 1,
                              padding: '7px 10px', borderRadius: '6px', cursor: 'pointer',
                            }}
                          >🔖</button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── My Profile ── */}
        {activeTab === 'My Profile' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('worker.dashboard.myProfile.header')}
            </p>
            <div style={{ maxWidth: 520 }}>

              {/* Photo upload — always visible */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '1.75rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <Avatar initials={initials} size={80} photoUrl={profile?.profile_photo_url} />
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />
                <button
                  onClick={() => { setPhotoMsg(''); photoInputRef.current?.click(); }}
                  disabled={photoUploading}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 18px', borderRadius: '8px', cursor: photoUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: photoUploading ? 0.7 : 1 }}
                >
                  {photoUploading ? t('worker.dashboard.myProfile.uploading') : t('worker.dashboard.myProfile.uploadPhoto')}
                </button>
                {photoMsg && (
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: photoMsgIsError ? '#F87171' : GREEN }}>
                    {photoMsg}
                  </span>
                )}
              </div>

              {profileForm && (<>
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.firstNameLabel')}</label>
                  <input style={inp} value={profileForm.firstName} onChange={e => setPf('firstName', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.lastNameLabel')}</label>
                  <input style={inp} value={profileForm.lastName} onChange={e => setPf('lastName', e.target.value)} />
                </div>
              </div>
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.phoneLabel')}</label>
                  <input style={inp} type="tel" value={profileForm.phone} onChange={e => setPf('phone', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.zipLabel')}</label>
                  <input style={inp} value={profileForm.zip} onChange={e => setPf('zip', e.target.value)} />
                </div>
              </div>
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.cityLabel')}</label>
                  <input style={inp} placeholder="Chicago" value={profileForm.city} onChange={e => setPf('city', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('worker.dashboard.myProfile.stateLabel')}</label>
                  <select style={sel} value={profileForm.state} onChange={e => setPf('state', e.target.value)}>
                    <option value="">{t('worker.dashboard.myProfile.selectStatePlaceholder')}</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="WI">Wisconsin</option>
                    <option value="MI">Michigan</option>
                    <option value="OH">Ohio</option>
                    <option value="MO">Missouri</option>
                    <option value="IA">Iowa</option>
                    <option value="MN">Minnesota</option>
                  </select>
                </div>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('worker.dashboard.myProfile.commuteRadiusLabel')}</label>
                <select style={sel} value={profileForm.commuteRadius} onChange={e => setPf('commuteRadius', e.target.value)}>
                  <option value="">{t('worker.dashboard.myProfile.commuteRadiusNoPreference')}</option>
                  <option value="5">{t('worker.dashboard.myProfile.commuteRadiusMiles', { count: 5 })}</option>
                  <option value="10">{t('worker.dashboard.myProfile.commuteRadiusMiles', { count: 10 })}</option>
                  <option value="25">{t('worker.dashboard.myProfile.commuteRadiusMiles', { count: 25 })}</option>
                  <option value="50">{t('worker.dashboard.myProfile.commuteRadiusMiles', { count: 50 })}</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', marginTop: '6px' }}>
                  {t('worker.dashboard.myProfile.commuteRadiusHint')}
                </p>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('worker.dashboard.myProfile.tradeLabel')}</label>
                <select style={sel} value={profileForm.trade} onChange={e => setPf('trade', e.target.value)}>
                  <option value="">{t('worker.dashboard.myProfile.selectTradePlaceholder')}</option>
                  {TRADE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(`worker.tradeNames.${opt.key}`)}</option>)}
                </select>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('worker.dashboard.myProfile.availabilityLabel')}</label>
                <select style={sel} value={profileForm.availability} onChange={e => setPf('availability', e.target.value)}>
                  <option value="">{t('worker.dashboard.myProfile.selectAvailabilityPlaceholder')}</option>
                  <option value="Full-time">{t('worker.signup.step3.availabilityOptions.fullTime')}</option>
                  <option value="Part-time">{t('worker.signup.step3.availabilityOptions.partTime')}</option>
                  <option value="Contract/Freelance">{t('worker.signup.step3.availabilityOptions.contract')}</option>
                  <option value="Temporary">{t('worker.signup.step3.availabilityOptions.temporary')}</option>
                  <option value="Seasonal">{t('worker.signup.step3.availabilityOptions.seasonal')}</option>
                  <option value="Open to all">{t('worker.signup.step3.availabilityOptions.openToAll')}</option>
                </select>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('worker.dashboard.myProfile.unionStatusLabel')}</label>
                <select style={sel} value={profileForm.unionStatus} onChange={e => setPf('unionStatus', e.target.value)}>
                  <option value="">{t('worker.dashboard.myProfile.selectUnionStatusPlaceholder')}</option>
                  <option value="both">{t('worker.signup.step3.unionInterestOptions.both')}</option>
                  <option value="union">{t('worker.signup.step3.unionInterestOptions.union')}</option>
                  <option value="employer">{t('worker.signup.step3.unionInterestOptions.employer')}</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '8px' }}>
                <button
                  onClick={handleSaveProfile}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('worker.dashboard.myProfile.saveChanges')}
                </button>
                {profileSaveMsg && (
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: profileSaveMsgIsError ? '#F87171' : GREEN }}>
                    {profileSaveMsg}
                  </span>
                )}
              </div>
              <button
                onClick={handleGenerateResume}
                style={{ background: NAVY, border: '1px solid rgba(255,255,255,0.2)', color: WHITE, fontWeight: 700, fontSize: '0.88rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY, marginTop: '10px' }}
              >
                {t('worker.dashboard.myProfile.generateResume')}
              </button>
              </>)}
            </div>
          </div>
        )}

        {/* ── Experience ── */}
        {activeTab === 'Experience' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>

            {/* ── Cert status banners ── */}
            {certBanners.length > 0 && (
              <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {certBanners.map(banner => {
                  const isVerified = banner.status === 'verified';
                  return (
                    <div key={banner.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                      padding: '12px 16px', borderRadius: '8px',
                      backgroundColor: isVerified ? 'rgba(29,158,117,0.15)' : 'rgba(248,113,113,0.12)',
                      border: `1px solid ${isVerified ? 'rgba(29,158,117,0.4)' : 'rgba(248,113,113,0.4)'}`,
                    }}>
                      <span style={{ fontSize: '0.82rem', color: isVerified ? '#1D9E75' : '#F87171', fontWeight: 600, lineHeight: 1.4 }}>
                        {isVerified
                          ? t('worker.dashboard.experience.certVerifiedBanner', { name: banner.name })
                          : t('worker.dashboard.experience.certRejectedBanner', { name: banner.name })}
                      </span>
                      <button
                        onClick={() => {
                          const dismissedKey = `certBannersDismissed_${worker.id}`;
                          const dismissed = new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]'));
                          dismissed.add(banner.id);
                          localStorage.setItem(dismissedKey, JSON.stringify([...dismissed]));
                          setCertBanners(prev => prev.filter(b => b.id !== banner.id));
                        }}
                        style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', fontFamily: 'inherit', padding: '2px 6px' }}
                      >
                        {t('worker.dashboard.experience.dismiss')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Resume upload ── */}
            <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ marginBottom: '10px' }}>
                <p style={{ margin: '0 0 2px', fontSize: '0.82rem', fontWeight: 700, color: WHITE }}>
                  {t('worker.dashboard.experience.uploadResumeLabel')} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t('worker.signup.step4.optional')}</span>
                </p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)' }}>{t('worker.dashboard.experience.uploadResumeHint')}</p>
              </div>
              <input
                ref={resumeInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleResumeUpload(f); e.target.value = ''; }}
              />
              {resumeUrl ? (
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#1D9E75', fontWeight: 600 }}>{t('worker.dashboard.experience.resumeUploaded')}</span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <a href={resumeUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('worker.dashboard.experience.viewResume')}</a>
                    <button
                      type="button"
                      onClick={() => resumeInputRef.current?.click()}
                      disabled={resumeUploading}
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: WHITE, fontSize: '0.78rem', padding: '6px 12px', borderRadius: '6px', cursor: resumeUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: resumeUploading ? 0.6 : 1 }}
                    >
                      {resumeUploading ? t('worker.signup.step2.uploading') : t('worker.dashboard.experience.replace')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !resumeUploading && resumeInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setResumeDragOver(true); }}
                  onDragLeave={() => setResumeDragOver(false)}
                  onDrop={e => {
                    e.preventDefault();
                    setResumeDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleResumeUpload(file);
                  }}
                  style={{
                    border: `2px dashed ${resumeDragOver ? AMBER : 'rgba(255,255,255,0.2)'}`,
                    borderRadius: '8px',
                    padding: '20px 16px',
                    textAlign: 'center',
                    cursor: resumeUploading ? 'default' : 'pointer',
                    background: resumeDragOver ? 'rgba(245,166,35,0.05)' : 'transparent',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  {resumeUploading ? (
                    <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>{t('worker.signup.step2.uploading')}</div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>
                        {t('worker.dashboard.experience.dragDropResume')}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>{t('worker.dashboard.experience.pdfOnly')}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {t('worker.profileView.sectionWorkExperience')}
              </p>
              <button
                onClick={() => setShowExpForm(v => !v)}
                style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
              >
                {showExpForm ? t('common.cancel') : t('worker.dashboard.experience.addExperience')}
              </button>
            </div>

            {showExpForm && (
              <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={g2}>
                  <div>
                    <label style={lbl}>{t('worker.dashboard.experience.companyLabel')}</label>
                    <input style={inp} placeholder="Acme Electric" value={expForm.company} onChange={e => setExp('company', e.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>{t('worker.dashboard.experience.roleLabel')}</label>
                    <input style={inp} placeholder="Journeyman Electrician" value={expForm.role} onChange={e => setExp('role', e.target.value)} />
                  </div>
                </div>
                <div style={g2}>
                  <div>
                    <label style={lbl}>{t('worker.dashboard.experience.startYearLabel')}</label>
                    <input style={inp} type="number" placeholder="2020" value={expForm.start_year} onChange={e => setExp('start_year', e.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>{t('worker.dashboard.experience.endYearLabel')}</label>
                    <input style={{ ...inp, opacity: expForm.current ? 0.35 : 1 }} type="number" placeholder="2024" value={expForm.end_year} disabled={expForm.current} onChange={e => setExp('end_year', e.target.value)} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginBottom: '1rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={expForm.current} onChange={e => setExp('current', e.target.checked)} style={{ accentColor: AMBER, width: 15, height: 15 }} />
                  {t('worker.dashboard.experience.currentlyWorkHere')}
                </label>
                <div style={fld}>
                  <label style={lbl}>{t('worker.dashboard.apprenticeships.descriptionLabel')}</label>
                  <textarea
                    style={{ ...inp, height: '80px', resize: 'vertical' }}
                    placeholder={t('worker.dashboard.experience.descPlaceholder')}
                    value={expForm.description}
                    onChange={e => setExp('description', e.target.value)}
                  />
                </div>
                <button
                  onClick={handleAddExperience}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('worker.dashboard.experience.saveExperience')}
                </button>
              </div>
            )}

            {loadingExp ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px', marginTop: '24px' }}>{t('common.loading')}</div>
            ) : experience.length === 0 ? (
              <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '28px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>
                {t('worker.dashboard.experience.noExperienceYet')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {experience.map(exp => (
                  <div key={exp.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: WHITE }}>{exp.role}</div>
                        <div style={{ fontSize: '13px', color: AMBER, marginTop: '2px' }}>{exp.company}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.38)', marginTop: '4px' }}>
                          {exp.start_year} – {exp.current ? t('worker.profileView.present') : (exp.end_year || '—')}
                        </div>
                        {exp.description && (
                          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '8px', lineHeight: 1.5 }}>{exp.description}</div>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteExperience(exp.id)}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 12px', flexShrink: 0 }}
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Certifications ── */}
            {!loadingExp && (
              <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <p style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: WHITE, letterSpacing: '0.05em' }}>{t('worker.signup.step4.certificationsLabel')}</p>
                  <button
                    onClick={() => { setShowCertForm(v => !v); setCertFormMsg(''); }}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {showCertForm ? t('common.cancel') : t('worker.dashboard.experience.addCertification')}
                  </button>
                </div>

                {showCertForm && (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                    <div style={g2}>
                      <div>
                        <label style={lbl}>{t('worker.signup.step4.certNameLabel')} <span style={{ color: AMBER }}>*</span></label>
                        <input style={inp} placeholder="OSHA 30, AWS Certified Welder..." value={certForm.certification_name} onChange={e => setCert('certification_name', e.target.value)} />
                      </div>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.issuingOrgLabel')}</label>
                        <input style={inp} placeholder="OSHA, AWS, NCCER..." value={certForm.issuing_organization} onChange={e => setCert('issuing_organization', e.target.value)} />
                      </div>
                    </div>
                    <div style={g2}>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.issueDateLabel')}</label>
                        <input style={inp} type="date" value={certForm.issue_date} onChange={e => setCert('issue_date', e.target.value)} />
                      </div>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.expirationDateLabel')} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t('worker.signup.step4.optional')}</span></label>
                        <input style={inp} type="date" value={certForm.expiry_date} onChange={e => setCert('expiry_date', e.target.value)} />
                      </div>
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.certificateNumberLabel')} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t('worker.signup.step4.optional')}</span></label>
                      <input style={inp} placeholder="e.g. OSHA-123456" value={certForm.certificate_number} onChange={e => setCert('certificate_number', e.target.value)} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.uploadCertDocLabel')} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t('worker.signup.step4.optional')}</span></label>
                      <input
                        ref={certDocInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={e => setCertDocFile(e.target.files?.[0] || null)}
                      />
                      <div
                        onClick={() => certDocInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setCertDragOver(true); }}
                        onDragLeave={() => setCertDragOver(false)}
                        onDrop={e => {
                          e.preventDefault();
                          setCertDragOver(false);
                          const file = e.dataTransfer.files?.[0];
                          if (file) setCertDocFile(file);
                        }}
                        style={{
                          border: `2px dashed ${certDragOver ? AMBER : 'rgba(255,255,255,0.2)'}`,
                          borderRadius: '8px',
                          padding: '20px 16px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: certDragOver ? 'rgba(245,166,35,0.05)' : 'transparent',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        {certDocFile ? (
                          <div>
                            <div style={{ fontSize: '0.85rem', color: AMBER, fontWeight: 600 }}>{certDocFile.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                              {t('worker.dashboard.experience.kbClickToChange', { kb: (certDocFile.size / 1024).toFixed(0) })}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>
                              {t('worker.dashboard.experience.dragDropCertDoc')}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>{t('worker.dashboard.experience.fileTypesDocs')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={handleAddCert}
                        disabled={certDocUploading}
                        style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: certDocUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: certDocUploading ? 0.7 : 1 }}
                      >
                        {certDocUploading ? t('worker.signup.step2.uploading') : t('worker.dashboard.experience.saveCertification')}
                      </button>
                      {certFormMsg && (
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: certFormMsgIsError ? '#F87171' : GREEN }}>
                          {certFormMsg}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {certifications.length === 0 ? (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '28px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>
                    {t('worker.dashboard.experience.noCertsYet')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {certifications.map(cert => (
                      <div key={cert.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: WHITE }}>{cert.certification_name}</span>
                              {cert.verification_status === 'verified' && (
                                <span style={{ backgroundColor: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>{t('worker.profileView.verified')}</span>
                              )}
                              {cert.verification_status === 'pending' && (
                                <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>{t('worker.dashboard.apprenticeships.statusPendingReview')}</span>
                              )}
                              {cert.verification_status === 'rejected' && (
                                <span style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>{t('worker.dashboard.experience.notVerified')}</span>
                              )}
                              {(!cert.verification_status || cert.verification_status === 'unverified') && (
                                <span style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>{t('worker.dashboard.experience.unverified')}</span>
                              )}
                            </div>
                            {cert.issuing_organization && (
                              <div style={{ fontSize: '13px', color: AMBER, marginTop: '2px' }}>{cert.issuing_organization}</div>
                            )}
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.38)', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              {cert.issue_date  && <span>{t('worker.dashboard.experience.issuedOn', { date: fmtDate(cert.issue_date) })}</span>}
                              {cert.expiry_date && <span>{t('worker.dashboard.experience.expiresOn', { date: fmtDate(cert.expiry_date) })}</span>}
                              {cert.certificate_number && <span>#{cert.certificate_number}</span>}
                            </div>
                            {cert.document_url && (
                              <div style={{ marginTop: '6px' }}>
                                <a href={cert.document_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('worker.dashboard.experience.viewDocument')}</a>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteCert(cert.id)}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 12px', flexShrink: 0 }}
                          >×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Licenses ── */}
            {!loadingExp && (
              <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <p style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: WHITE, letterSpacing: '0.05em' }}>{t('worker.signup.step4.licensesLabel')}</p>
                  <button
                    onClick={() => { setShowLicenseForm(v => !v); setLicFormMsg(''); }}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {showLicenseForm ? t('common.cancel') : t('worker.dashboard.experience.addLicense')}
                  </button>
                </div>

                {showLicenseForm && (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                    <div style={g2}>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.licenseTypeLabel')} <span style={{ color: AMBER }}>*</span></label>
                        <input style={inp} placeholder="Journeyman Electrician, CDL Class A..." value={licenseForm.license_name} onChange={e => setLic('license_name', e.target.value)} />
                      </div>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.licenseNumberLabel')}</label>
                        <input style={inp} placeholder="e.g. IL-123456" value={licenseForm.license_number} onChange={e => setLic('license_number', e.target.value)} />
                      </div>
                    </div>
                    <div style={g2}>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.myProfile.stateLabel')}</label>
                        <select style={sel} value={licenseForm.state} onChange={e => setLic('state', e.target.value)}>
                          <option value="">{t('worker.dashboard.myProfile.selectStatePlaceholder')}</option>
                          <option value="IL">Illinois</option>
                          <option value="IN">Indiana</option>
                          <option value="WI">Wisconsin</option>
                          <option value="MI">Michigan</option>
                          <option value="OH">Ohio</option>
                          <option value="MO">Missouri</option>
                          <option value="IA">Iowa</option>
                          <option value="MN">Minnesota</option>
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>{t('worker.dashboard.experience.issueDateLabel')}</label>
                        <input style={inp} type="date" value={licenseForm.issue_date} onChange={e => setLic('issue_date', e.target.value)} />
                      </div>
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.expirationDateLabel')}</label>
                      <input style={inp} type="date" value={licenseForm.expiry_date} onChange={e => setLic('expiry_date', e.target.value)} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.uploadLicenseDocLabel')} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t('worker.signup.step4.optional')}</span></label>
                      <input
                        ref={licDocInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={e => setLicDocFile(e.target.files?.[0] || null)}
                      />
                      <div
                        onClick={() => licDocInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setLicDragOver(true); }}
                        onDragLeave={() => setLicDragOver(false)}
                        onDrop={e => {
                          e.preventDefault();
                          setLicDragOver(false);
                          const file = e.dataTransfer.files?.[0];
                          if (file) setLicDocFile(file);
                        }}
                        style={{
                          border: `2px dashed ${licDragOver ? AMBER : 'rgba(255,255,255,0.2)'}`,
                          borderRadius: '8px',
                          padding: '20px 16px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: licDragOver ? 'rgba(245,166,35,0.05)' : 'transparent',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        {licDocFile ? (
                          <div>
                            <div style={{ fontSize: '0.85rem', color: AMBER, fontWeight: 600 }}>{licDocFile.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                              {t('worker.dashboard.experience.kbClickToChange', { kb: (licDocFile.size / 1024).toFixed(0) })}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>
                              {t('worker.dashboard.experience.dragDropLicenseDoc')}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>{t('worker.dashboard.experience.fileTypesDocs')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={handleAddLicense}
                        disabled={licDocUploading}
                        style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: licDocUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: licDocUploading ? 0.7 : 1 }}
                      >
                        {licDocUploading ? t('worker.signup.step2.uploading') : t('worker.dashboard.experience.saveLicense')}
                      </button>
                      {licFormMsg && (
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: licFormMsgIsError ? '#F87171' : GREEN }}>
                          {licFormMsg}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {licenses.length === 0 ? (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '28px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>
                    {t('worker.dashboard.experience.noLicensesYet')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {licenses.map(lic => (
                      <div key={lic.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: WHITE }}>{lic.license_name}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.38)', marginTop: '4px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              {lic.state          && <span>{lic.state}</span>}
                              {lic.license_number && <span>#{lic.license_number}</span>}
                              {lic.issue_date     && <span>{t('worker.dashboard.experience.issuedOn', { date: fmtDate(lic.issue_date) })}</span>}
                              {lic.expiry_date    && <span>{t('worker.dashboard.experience.expiresOn', { date: fmtDate(lic.expiry_date) })}</span>}
                            </div>
                            {lic.document_url && (
                              <div style={{ marginTop: '6px' }}>
                                <a href={lic.document_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('worker.dashboard.experience.viewDocument')}</a>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteLicense(lic.id)}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 12px', flexShrink: 0 }}
                          >×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Portfolio ── */}
            {!loadingExp && (
              <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <p style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: WHITE, letterSpacing: '0.05em' }}>{t('worker.profileView.sectionPortfolio')}</p>
                  <button
                    onClick={() => { setShowPortfolioForm(v => !v); setPortfolioLinkMode(false); setPortfolioVideoFile(null); setPortfolioForm({ title: '', description: '', videoUrl: '' }); }}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 16px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {showPortfolioForm ? t('common.cancel') : t('worker.dashboard.experience.addVideo')}
                  </button>
                </div>

                {showPortfolioForm && (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.titleRequired')}</label>
                      <input style={inp} placeholder="e.g. MIG Welding on Carbon Steel" value={portfolioForm.title} onChange={e => setPortfolioForm(p => ({ ...p, title: e.target.value }))} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('worker.dashboard.experience.descriptionOptional')}</label>
                      <textarea style={{ ...inp, minHeight: '72px', resize: 'vertical' }} placeholder={t('worker.dashboard.experience.describeShowing')} value={portfolioForm.description} onChange={e => setPortfolioForm(p => ({ ...p, description: e.target.value }))} />
                    </div>

                    {/* Toggle: upload vs link */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <button
                        onClick={() => { setPortfolioLinkMode(false); setPortfolioVideoFile(null); setPortfolioForm(p => ({ ...p, videoUrl: '' })); }}
                        style={{ flex: 1, padding: '8px', borderRadius: '7px', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${!portfolioLinkMode ? AMBER : 'rgba(255,255,255,0.15)'}`, background: !portfolioLinkMode ? 'rgba(245,166,35,0.1)' : 'transparent', color: !portfolioLinkMode ? AMBER : 'rgba(255,255,255,0.45)' }}
                      >{t('worker.dashboard.experience.uploadVideoToggle')}</button>
                      <button
                        onClick={() => { setPortfolioLinkMode(true); setPortfolioVideoFile(null); }}
                        style={{ flex: 1, padding: '8px', borderRadius: '7px', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${portfolioLinkMode ? AMBER : 'rgba(255,255,255,0.15)'}`, background: portfolioLinkMode ? 'rgba(245,166,35,0.1)' : 'transparent', color: portfolioLinkMode ? AMBER : 'rgba(255,255,255,0.45)' }}
                      >{t('worker.dashboard.experience.pasteLinkToggle')}</button>
                    </div>

                    {!portfolioLinkMode && (
                      <div style={fld}>
                        <input
                          ref={portfolioVideoInputRef}
                          type="file"
                          accept="video/mp4,video/quicktime,video/x-msvideo"
                          style={{ display: 'none' }}
                          onChange={e => setPortfolioVideoFile(e.target.files?.[0] || null)}
                        />
                        <button
                          onClick={() => portfolioVideoInputRef.current?.click()}
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)', color: WHITE, borderRadius: '8px', padding: '9px 16px', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY }}
                        >{t('worker.dashboard.experience.chooseFileVideo')}</button>
                        {portfolioVideoFile && (
                          <span style={{ marginLeft: '10px', fontSize: '0.82rem', color: AMBER, fontWeight: 500 }}>{portfolioVideoFile.name}</span>
                        )}
                        {portfolioVideoUploading && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${portfolioVideoProgress}%`, background: AMBER, transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', display: 'block' }}>{t('worker.signup.step2.uploading')}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {portfolioLinkMode && (
                      <div style={fld}>
                        <label style={lbl}>{t('worker.dashboard.experience.youtubeVimeoLabel')}</label>
                        <input style={inp} placeholder="https://www.youtube.com/watch?v=..." value={portfolioForm.videoUrl} onChange={e => setPortfolioForm(p => ({ ...p, videoUrl: e.target.value }))} />
                      </div>
                    )}

                    <button
                      onClick={handleAddPortfolio}
                      disabled={portfolioVideoUploading}
                      style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: portfolioVideoUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: portfolioVideoUploading ? 0.7 : 1 }}
                    >
                      {portfolioVideoUploading ? t('worker.signup.step2.uploading') : t('worker.dashboard.experience.savePortfolioItem')}
                    </button>
                  </div>
                )}

                {portfolio.length === 0 ? (
                  <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '28px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>
                    {t('worker.dashboard.experience.noPortfolioYet')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {portfolio.map(item => {
                      const ytId     = item.video_type === 'youtube' ? extractYouTubeId(item.video_url || '') : null;
                      const vimeoId  = item.video_type === 'vimeo'   ? extractVimeoId(item.video_url || '')   : null;
                      return (
                        <div key={item.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden' }}>
                          {ytId && (
                            <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                              <iframe src={`https://www.youtube.com/embed/${ytId}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={item.title} />
                            </div>
                          )}
                          {vimeoId && (
                            <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                              <iframe src={`https://player.vimeo.com/video/${vimeoId}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={item.title} />
                            </div>
                          )}
                          {item.video_type === 'upload' && item.video_url && (
                            <video controls style={{ width: '100%', display: 'block', maxHeight: '280px', background: '#000' }} src={item.video_url} />
                          )}
                          <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: WHITE, marginBottom: item.description ? '4px' : 0 }}>{item.title}</div>
                              {item.description && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{item.description}</div>}
                            </div>
                            <button
                              onClick={() => handleDeletePortfolio(item.id)}
                              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 12px', flexShrink: 0 }}
                            >×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── School Finder ── */}
        {activeTab === 'School Finder' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <ResourceFinder type="school" onViewTradePay={goToTradePay} />
          </div>
        )}

        {/* ── Union Finder ── */}
        {activeTab === 'Union Finder' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <ResourceFinder type="union" onViewTradePay={goToTradePay} />
          </div>
        )}

        {/* ── Licensing & Certs ── */}
        {activeTab === 'Licensing & Certs' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <LicensingCerts onViewTradeHub={setActiveTradeHub} />
          </div>
        )}

        {/* ── Career Pay Guide ── */}
        {activeTab === 'Career Pay Guide' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <CareerPayGuide
              initialTrade={payGuideInitialTrade}
              onInitialTradeApplied={() => setPayGuideInitialTrade(null)}
              onViewTradeHub={setActiveTradeHub}
            />
          </div>
        )}

        {/* ── Settings ── */}
        {activeTab === 'Settings' && (
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('common.settings')}
            </p>

            <div style={{ maxWidth: 480 }}>
              {/* Change Password */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('worker.dashboard.settings.changePasswordTitle')}</h3>
                <div style={fld}>
                  <label style={lbl}>{t('worker.dashboard.settings.newPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} />
                </div>
                <div style={fld}>
                  <label style={lbl}>{t('worker.dashboard.settings.confirmNewPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={pwForm.confirmPw} onChange={e => setPwForm(p => ({ ...p, confirmPw: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={handleChangePassword}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {t('worker.dashboard.settings.updatePassword')}
                  </button>
                  {pwMsg && (
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: pwMsgIsSuccess ? GREEN : '#F87171' }}>
                      {pwMsg}
                    </span>
                  )}
                </div>
              </div>

              {/* Notifications */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('worker.dashboard.settings.notificationsTitle')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                  <div>
                    <p style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE }}>{t('worker.dashboard.settings.emailNotifications')}</p>
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.38)', marginTop: '3px', lineHeight: 1.5 }}>{t('worker.dashboard.settings.emailNotifDesc')}</p>
                  </div>
                  <div
                    onClick={handleToggleEmailNotif}
                    style={{
                      width: 44, height: 24, borderRadius: 12, flexShrink: 0,
                      backgroundColor: emailNotif ? AMBER : 'rgba(255,255,255,0.15)',
                      cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, left: emailNotif ? 23 : 3,
                      width: 18, height: 18, borderRadius: '50%', backgroundColor: WHITE,
                      transition: 'left 0.2s',
                    }} />
                  </div>
                </div>
              </div>

              {/* My Organizations -- self-service leave, no admin involvement needed. */}
              {myMemberships.length > 0 && (
                <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginTop: '16px' }}>
                  <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>
                    {t('worker.settings.myOrganizationsHeading')}
                  </h3>
                  <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', marginBottom: '14px' }}>
                    {t('worker.settings.myOrganizationsNote')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {myMemberships.map(m => (
                      <div key={m.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
                        background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 14px',
                      }}>
                        <span style={{ fontSize: '0.86rem', fontWeight: 600, color: WHITE, flex: 1, minWidth: '120px' }}>{m.name}</span>
                        <button
                          onClick={() => handleLeaveOrganization(m)}
                          disabled={leavingOrgId === m.id}
                          style={{
                            background: 'transparent', border: '1px solid rgba(248,113,113,0.35)', color: '#F87171',
                            fontFamily: FONT_BODY, fontSize: '0.78rem', fontWeight: 700, padding: '6px 14px',
                            borderRadius: '6px', flexShrink: 0, whiteSpace: 'nowrap',
                            cursor: leavingOrgId === m.id ? 'default' : 'pointer',
                            opacity: leavingOrgId === m.id ? 0.5 : 1,
                          }}
                        >
                          {t('worker.settings.leaveOrgButton')}
                        </button>
                      </div>
                    ))}
                  </div>
                  {leaveError && (
                    <p style={{ fontSize: '0.8rem', color: '#F87171', marginTop: '10px', marginBottom: 0 }}>{leaveError}</p>
                  )}
                </div>
              )}

              {/* Sign Out */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginTop: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>{t('common.signOut')}</h3>
                <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', marginBottom: '14px' }}>{t('worker.settings.signOutNote')}</p>
                <button
                  onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
                  style={{
                    background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)',
                    color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                    padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('common.signOut')}
                </button>
              </div>

              {/* Delete Account */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '28px', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowDeleteAccountModal(true)}
                  style={{
                    background: 'transparent', border: '1px solid #DC2626', color: '#DC2626',
                    fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                    padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('worker.dashboard.settings.deleteAccount')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAccountModal && (
          <DeleteAccountModal
            supabase={supabase}
            userId={worker.id}
            role="worker"
            onClose={() => setShowDeleteAccountModal(false)}
          />
        )}

        {cropFile && (
          <ImageCropModal
            file={cropFile}
            onCancel={() => setCropFile(null)}
            onConfirm={handlePhotoCropConfirm}
          />
        )}

        {isMobile && !(activeTab === 'Messages' && chatThreadOpen) && (
          <div style={{ padding: '20px 16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <LegalLinksRow fontSize="0.7rem" />
            <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
          </div>
        )}

      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="dash-bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 'calc(58px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box',
        backgroundColor: NAVY_MID, borderTop: '1px solid rgba(255,255,255,0.08)',
        zIndex: 200, alignItems: 'center', justifyContent: 'space-around',
      }}>
        {(() => {
          const closeAllGroups = () => {
            setMobileProfileOpen(false);
            setMobileJobsOpen(false);
            setMobileResourcesOpen(false);
          };
          return NAV_ITEMS.map(({ key, label, Icon }) => {
            const isGroup = key === 'jobs' || key === 'resources' || key === 'profile';
            const groupOpen = key === 'jobs' ? mobileJobsOpen : key === 'resources' ? mobileResourcesOpen : key === 'profile' ? mobileProfileOpen : false;
            const anyGroupOpen = mobileJobsOpen || mobileResourcesOpen || mobileProfileOpen;
            const tab = key === 'dashboard' ? 'Dashboard' : key === 'messages' ? 'Messages' : null;
            const isActive = isGroup ? groupOpen : (!anyGroupOpen && activeTab === tab);
            const showDot = (key === 'messages' && mobileMessageBadge > 0) || (key === 'jobs' && (appsBadge || jobsBadge));
            const handleClick = () => {
              closeAllGroups();
              if (key === 'jobs') {
                setMobileJobsOpen(true);
                setActiveTab('Jobs');
              } else if (key === 'resources') {
                setMobileResourcesOpen(true);
                setActiveTab('School Finder');
              } else if (key === 'profile') {
                setMobileProfileOpen(true);
                setActiveTab('My Profile');
              } else {
                setActiveTab(tab);
              }
            };
            return (
              <button key={key} type="button" onClick={handleClick} aria-current={isActive ? 'page' : undefined} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                padding: '4px 4px', cursor: 'pointer', flex: 1, position: 'relative',
                background: 'none', border: 'none', margin: 0, font: 'inherit',
              }}>
                <Icon color={isActive ? AMBER : 'rgba(255,255,255,0.38)'} />
                {showDot && (
                  <div style={{ position: 'absolute', top: 0, left: '50%', marginLeft: '4px', width: 7, height: 7, borderRadius: '50%', backgroundColor: AMBER }} />
                )}
                <span style={{ fontSize: '0.52rem', fontWeight: 600, color: isActive ? AMBER : 'rgba(255,255,255,0.38)', textAlign: 'center' }}>
                  {label}
                </span>
              </button>
            );
          });
        })()}
      </nav>

      {/* ── Accept Offer Confirmation Modal ── */}
      {acceptConfirmModal && (() => {
        const { app, offer } = acceptConfirmModal;
        const companyName = app.employer?.company_name || '';
        const jobTitle    = offer.job_title || app.job?.job_title || '';
        // job_offers.start_date is a Postgres `date`: parsed as a local date (parseExamDate), since
        // new Date("YYYY-MM-DD") is UTC midnight and showed the previous day in US time zones.
        const startDateStr = parseExamDate(offer.start_date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) || '';
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}>
            <div style={{
              backgroundColor: NAVY_MID, borderRadius: '14px',
              width: '100%', maxWidth: '480px',
              maxHeight: '90vh', overflowY: 'auto',
              padding: '28px 24px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            }}>
              {/* Company logo or name at top */}
              {app.employer?.logo_url ? (
                <div style={{ marginBottom: '18px' }}>
                  <ZoomableImg src={app.employer.logo_url} alt={companyName} style={{ height: '52px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                </div>
              ) : companyName ? (
                <div style={{ fontFamily: FONT_HEAD, fontSize: '1.2rem', color: AMBER, letterSpacing: '0.06em', marginBottom: '10px' }}>{companyName}</div>
              ) : null}

              <div style={{ fontFamily: FONT_HEAD, fontSize: '1.8rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>
                {t('worker.dashboard.acceptOfferModal.title')}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginBottom: '22px' }}>
                {t('worker.dashboard.acceptOfferModal.reviewDetails')}
              </div>

              {/* Offer summary */}
              <div style={{ backgroundColor: NAVY, borderRadius: '10px', padding: '18px 20px', marginBottom: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {jobTitle && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{t('worker.dashboard.acceptOfferModal.jobTitleLabel')}</div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: WHITE }}>{jobTitle}</div>
                  </div>
                )}
                {companyName && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{t('worker.dashboard.experience.companyLabel')}</div>
                    <div style={{ fontSize: '14px', color: WHITE }}>{companyName}</div>
                  </div>
                )}
                {offer.offered_pay && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{t('worker.dashboard.acceptOfferModal.payRateLabel')}</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: AMBER }}>
                      {offer.offered_pay}{offer.pay_schedule ? ` · ${offer.pay_schedule}` : ''}
                    </div>
                  </div>
                )}
                {startDateStr && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{t('worker.dashboard.applications.startDateLabel')}</div>
                    <div style={{ fontSize: '14px', color: WHITE }}>{startDateStr}</div>
                  </div>
                )}
                {offer.work_location && (
                  <div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{t('worker.dashboard.applications.workLocationLabel')}</div>
                    <div style={{ fontSize: '14px', color: WHITE }}>{offer.work_location}</div>
                  </div>
                )}
              </div>

              {/* Checkbox */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginBottom: '20px' }}>
                <input
                  type="checkbox"
                  checked={acceptConfirmChecked}
                  onChange={e => setAcceptConfirmChecked(e.target.checked)}
                  style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                />
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                  {t('worker.dashboard.acceptOfferModal.confirmTermsCheckbox')}
                </span>
              </label>

              {/* Signature field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '7px' }}>
                  {t('worker.dashboard.acceptOfferModal.typeNameToSign')}
                </label>
                <input
                  type="text"
                  value={acceptSignature}
                  onChange={e => setAcceptSignature(e.target.value)}
                  placeholder="e.g. Marcus Rivera"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    backgroundColor: WHITE, color: NAVY,
                    border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: '8px',
                    fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 500,
                    padding: '10px 14px', outline: 'none',
                  }}
                />
                {acceptSignature.trim() && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(245,166,35,0.7)', fontStyle: 'italic' }}>
                    {t('worker.dashboard.acceptOfferModal.signedOn', {
                      name: acceptSignature.trim(),
                      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                    })}
                  </div>
                )}
              </div>

              {/* Buttons */}
              {(() => {
                const canAccept = acceptConfirmChecked && acceptSignature.trim() !== '';
                return (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => { setAcceptConfirmModal(null); setAcceptConfirmChecked(false); setAcceptSignature(''); }}
                      style={{ flex: 1, background: 'transparent', border: '1.5px solid rgba(245,166,35,0.6)', color: AMBER, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, padding: '11px 0', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      {t('worker.dashboard.acceptOfferModal.goBack')}
                    </button>
                    <button
                      onClick={() => handleAcceptOffer(app, offer)}
                      disabled={!canAccept}
                      style={{ flex: 1, background: canAccept ? '#10B981' : 'rgba(16,185,129,0.3)', border: 'none', color: WHITE, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700, padding: '11px 0', borderRadius: '8px', cursor: canAccept ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}
                    >
                      {t('worker.dashboard.acceptOfferModal.confirmAndAccept')}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* ── Dismiss Job (Not Interested) Modal ── */}
      {dismissJobModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            backgroundColor: NAVY_MID, borderRadius: '14px',
            width: '100%', maxWidth: '420px',
            maxHeight: '90vh', overflowY: 'auto',
            padding: '24px 22px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '4px' }}>
              {t('worker.dashboard.dismissModal.notInterestedTitle')}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginBottom: '18px' }}>
              {t('worker.dashboard.dismissModal.notInterestedBody')}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: dismissReason === 'Other' ? '12px' : '20px' }}>
              {DISMISS_REASON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDismissReason(dismissReason === opt.value ? '' : opt.value)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: dismissReason === opt.value ? 'rgba(245,166,35,0.12)' : 'transparent',
                    border: `1.5px solid ${dismissReason === opt.value ? AMBER : 'rgba(255,255,255,0.12)'}`,
                    color: dismissReason === opt.value ? AMBER : 'rgba(255,255,255,0.75)',
                    fontSize: '13px', fontWeight: 600, padding: '10px 14px', borderRadius: '8px',
                    cursor: 'pointer', fontFamily: FONT_BODY,
                  }}
                >{t(`worker.dashboard.dismissModal.${opt.key}`)}</button>
              ))}
            </div>

            {dismissReason === 'Other' && (
              <div style={{ marginBottom: '20px' }}>
                <textarea
                  value={dismissDetail}
                  onChange={e => setDismissDetail(e.target.value)}
                  placeholder={t('worker.dashboard.dismissModal.tellUsMorePlaceholder')}
                  rows={3}
                  style={{
                    width: '100%', boxSizing: 'border-box', resize: 'vertical',
                    backgroundColor: NAVY, color: WHITE,
                    border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                    padding: '10px 12px', fontSize: '13px', fontFamily: FONT_BODY, outline: 'none',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setDismissJobModal(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >{t('worker.dashboard.dismissModal.cancel')}</button>
              <button
                onClick={handleConfirmDismiss}
                disabled={dismissSubmitting}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', background: AMBER, color: NAVY, fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 700, cursor: dismissSubmitting ? 'default' : 'pointer', opacity: dismissSubmitting ? 0.7 : 1 }}
              >{dismissSubmitting ? t('worker.dashboard.dismissModal.dismissing') : t('worker.dashboard.dismissModal.dismissJob')}</button>
            </div>
          </div>
        </div>
      )}

      {activeTradeHub && (
        <TradeHub hubTrade={activeTradeHub} onClose={() => setActiveTradeHub(null)} />
      )}

      {openCompare === 'jobs' && (
        <WorkerJobCompare
          jobs={allJobs.filter(j => jobCompare.isSelected(j.id))}
          onRemove={jobCompare.remove}
          onClear={jobCompare.clear}
          onClose={() => setOpenCompare(null)}
        />
      )}
      {openCompare === 'offers' && (
        <WorkerOfferCompare
          applications={applications.filter(a => a.hasReceivedOffer && offerCompare.isSelected(a.id))}
          onRemove={offerCompare.remove}
          onClear={offerCompare.clear}
          onClose={() => setOpenCompare(null)}
        />
      )}
      {openCompare === 'apprenticeships' && (
        <WorkerApprenticeshipCompare
          apprenticeships={apprenticeships.filter(a => !a.noLongerOpen && apprCompare.isSelected(a.id))}
          examsByAppr={apprExamsMap}
          onRemove={apprCompare.remove}
          onClear={apprCompare.clear}
          onClose={() => setOpenCompare(null)}
        />
      )}

      {hireCelebration && (
        <HireCelebrationModal
          companyName={hireCelebration.companyName}
          jobTitle={hireCelebration.jobTitle}
          hireDate={hireCelebration.hireDate}
          onClose={() => setHireCelebration(null)}
          onViewDetails={() => {
            setHireCelebration(null);
            navigate(`/company/${hireCelebration.employerId}`);
          }}
          onMessageEmployer={async () => {
            const convId = await startConversation(supabase, worker.id, hireCelebration.employerId, hireCelebration.jobId, 'worker');
            setHireCelebration(null);
            if (!convId) return;
            setOpenConversationId(convId);
            setActiveTab('Messages');
          }}
        />
      )}
    </div>
  );
}
